import i18n from 'i18next';
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Edit,
  Eye,
  MousePointer,
  DollarSign,
  Target,
  Calendar,
  Search,
  Plus,
  Ban,
  Check,
  X,
  RefreshCw,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  TrendingUp,
  ChevronRight,
  Loader2,
  Settings,
  AlertTriangle,
  ExternalLink,
  CheckCircle2,
  XCircle,
  MinusCircle,
  Zap,
  BarChart3,
  Play,
  Pause,
  Megaphone,
  Palette,
  Sparkles,
  Lightbulb,
  Instagram,
  Download,
  FileText,
  FileSpreadsheet,
  ChevronDown,
  Pencil,
  Code,
  Trash2
} from 'lucide-react';
import {
  campaignService,
  GoogleCampaignSettings,
  SearchTermReport,
  analyzeCampaign as analyzeCampaignApi,
  applyGoogleRecommendation as applyGoogleRecApi,
  syncCampaignMetrics as syncCampaignMetricsApi,
  CampaignAnalysisResult,
  RecommendationCard,
  GoogleRecommendation,
  ConversionSnippet
} from '../../services/campaignService';
import { adService } from '../../services/adService';
import api from '../../services/api';
import { useProject } from '../../contexts/ProjectContext';
import { Card, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import type { PlatformResult } from '../../components/campaigns/ToggleResultModal';
import Modal from '../../components/ui/Modal';
import CampaignKeywordsHub from '../../components/campaigns/CampaignKeywordsHub';
import CampaignAutomationHub from '../../components/campaigns/CampaignAutomationHub';
import CampaignAnalytics from '../../components/campaigns/CampaignAnalytics';
import { Campaign, Ad } from '../../types';
import CampaignMediaGallery from '../../components/campaigns/CampaignMediaGallery';
import GoogleAiMaxToggle from '../../components/campaigns/GoogleAiMaxToggle';
import GooglePortfolioBidding from '../../components/campaigns/rmf/GooglePortfolioBidding';
import GoogleBiddingReport from '../../components/campaigns/rmf/GoogleBiddingReport';
import { usePortfolioStrategies } from '../../components/campaigns/rmf/usePortfolioStrategies';
import { searchTermMatchLabel, biddingTypeLabel } from '../../components/campaigns/rmf/rmfLabels';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

type TabType = 'overview' | 'ads' | 'keywords' | 'search-terms' | 'automation' | 'analytics' | 'media' | 'recommendations';

// Objetivo vem cru das plataformas ("conversions", "OUTCOME_SALES"...): mostra o nome em português.
const OBJETIVOS: Record<string, string> = {
  conversions: 'sales', sales: 'sales', OUTCOME_SALES: 'sales', leads: 'leads', OUTCOME_LEADS: 'leads',
  traffic: 'traffic', OUTCOME_TRAFFIC: 'traffic', awareness: 'awareness', OUTCOME_AWARENESS: 'awareness',
  engagement: 'engagement', OUTCOME_ENGAGEMENT: 'engagement', remarketing: 'remarketing',
};
const objetivoLabel = (obj?: string) => {
  if (!obj) return '';
  const key = OBJETIVOS[obj] || OBJETIVOS[obj.toLowerCase()];
  const cru = obj.replace(/^OUTCOME_/, '').replace(/_/g, ' ').toLowerCase();
  return key ? String(i18n.t(`campaigns.objectives.${key}`, { defaultValue: cru })) : cru;
};

const CampaignDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { currentProject } = useProject();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Filtro de período pro gráfico de Performance no Dashboard (default: últimos 30 dias)
  const [chartStartDate, setChartStartDate] = useState<string>(() => new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]);
  const [chartEndDate, setChartEndDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [chartLoading, setChartLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isToggling, setIsToggling] = useState(false);
  const [inlinePlatformResults, setInlinePlatformResults] = useState<Record<string, PlatformResult> | null>(null);
  const [pushingPlatform, setPushingPlatform] = useState<string | null>(null);

  // Ads state
  const [ads, setAds] = useState<Ad[]>([]);
  const [loadingAds, setLoadingAds] = useState(false);
  const [adsStatusFilter, setAdsStatusFilter] = useState<'all' | 'active' | 'paused' | 'inactive'>('all');
  const [togglingAdIds, setTogglingAdIds] = useState<Set<string>>(new Set());
  const [viewingAd, setViewingAd] = useState<Ad | null>(null);
  const [platformStatus, setPlatformStatus] = useState<any>(null);
  const [loadingPlatformStatus, setLoadingPlatformStatus] = useState(false);

  // Google Ads live settings (estratégia de lance + metas de conversão).
  // Buscado uma vez quando a campanha é Google. `googleSettingsCode` carrega o
  // motivo quando não há dados (não conectado / reconectar / não-google).
  const [googleSettings, setGoogleSettings] = useState<GoogleCampaignSettings | null>(null);
  const [loadingGoogleSettings, setLoadingGoogleSettings] = useState(false);
  const [googleSettingsCode, setGoogleSettingsCode] = useState<string | null>(null);
  // Edição inline da estratégia de lance (push direto pro Google Ads).
  const [editingBid, setEditingBid] = useState(false);
  const [bidStrategy, setBidStrategy] = useState<string>('maximize_clicks');
  const [bidTargetCpa, setBidTargetCpa] = useState<string>('');
  const [bidTargetRoas, setBidTargetRoas] = useState<string>('');
  const [savingBid, setSavingBid] = useState(false);
  // Estratégia padrão (só desta campanha) x de portfólio (compartilhada entre campanhas).
  const [bidMode, setBidMode] = useState<'standard' | 'portfolio'>('standard');
  const { portfolios, loading: loadingPortfolios, reload: reloadPortfolios } = usePortfolioStrategies(!!googleSettings);
  const [removingAdIds, setRemovingAdIds] = useState<Set<string>>(new Set());
  // Edição inline das metas de conversão (quais CATEGORIAS contam para o lance).
  const [editingGoals, setEditingGoals] = useState(false);
  const [goalCats, setGoalCats] = useState<Record<string, boolean>>({});
  const [savingGoals, setSavingGoals] = useState(false);
  // Snippet de conversão (modal): visualizar a tag/snippet de uma meta.
  const [snippetModalOpen, setSnippetModalOpen] = useState(false);
  const [snippetLoading, setSnippetLoading] = useState(false);
  const [snippetData, setSnippetData] = useState<ConversionSnippet | null>(null);
  const [snippetName, setSnippetName] = useState('');
  const [selectedSnippetIdx, setSelectedSnippetIdx] = useState(0);
  // Criar nova meta de conversão (modal).
  const [newGoalModalOpen, setNewGoalModalOpen] = useState(false);
  const [newGoalName, setNewGoalName] = useState('');
  const [newGoalCategory, setNewGoalCategory] = useState('DEFAULT');
  const [creatingGoal, setCreatingGoal] = useState(false);

  const openSnippetModal = async (actionId: string, name: string) => {
    if (!id) return;
    setSnippetName(name);
    setSnippetData(null);
    setSelectedSnippetIdx(0);
    setSnippetModalOpen(true);
    setSnippetLoading(true);
    try {
      const res = await campaignService.getConversionSnippet(id, actionId);
      if (res.success && res.data) {
        setSnippetData(res.data);
        // Default to the primary snippet (or first).
        const primaryIdx = res.data.primary
          ? res.data.snippets.findIndex(
              (s) => s.type === res.data!.primary!.type && s.format === res.data!.primary!.format
            )
          : -1;
        setSelectedSnippetIdx(primaryIdx >= 0 ? primaryIdx : 0);
      } else {
        toast.error(res.message || t('campaigns.googleSnippet.error', 'Falha ao carregar o snippet'));
        setSnippetModalOpen(false);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || t('campaigns.googleSnippet.error', 'Falha ao carregar o snippet'));
      setSnippetModalOpen(false);
    } finally {
      setSnippetLoading(false);
    }
  };

  const copySnippet = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text || '');
      toast.success(t('campaigns.googleSnippet.copied', 'Copiado!'));
    } catch {
      toast.error(t('campaigns.googleSnippet.error', 'Falha ao copiar'));
    }
  };

  const newGoalCategoryOptions: Array<{ value: string; label: string }> = [
    { value: 'DEFAULT', label: t('campaigns.googleNewGoal.cat.DEFAULT', 'Outra') },
    { value: 'PURCHASE', label: t('campaigns.googleNewGoal.cat.PURCHASE', 'Compra') },
    { value: 'SIGNUP', label: t('campaigns.googleNewGoal.cat.SIGNUP', 'Cadastro') },
    { value: 'SUBMIT_LEAD_FORM', label: t('campaigns.googleNewGoal.cat.SUBMIT_LEAD_FORM', 'Envio de formulário de lead') },
    { value: 'CONTACT', label: t('campaigns.googleNewGoal.cat.CONTACT', 'Contato') },
    { value: 'BOOK_APPOINTMENT', label: t('campaigns.googleNewGoal.cat.BOOK_APPOINTMENT', 'Agendamento') },
    { value: 'REQUEST_QUOTE', label: t('campaigns.googleNewGoal.cat.REQUEST_QUOTE', 'Solicitação de orçamento') },
    { value: 'DOWNLOAD', label: t('campaigns.googleNewGoal.cat.DOWNLOAD', 'Download') },
    { value: 'ADD_TO_CART', label: t('campaigns.googleNewGoal.cat.ADD_TO_CART', 'Adicionar ao carrinho') },
    { value: 'BEGIN_CHECKOUT', label: t('campaigns.googleNewGoal.cat.BEGIN_CHECKOUT', 'Início de checkout') },
  ];

  const openNewGoalModal = () => {
    setNewGoalName('');
    setNewGoalCategory('DEFAULT');
    setNewGoalModalOpen(true);
  };

  const handleCreateGoal = async () => {
    if (!id || !newGoalName.trim()) return;
    setCreatingGoal(true);
    try {
      const res = await campaignService.createConversion(id, { name: newGoalName.trim(), category: newGoalCategory });
      if (res.success) {
        toast.success(t('campaigns.googleNewGoal.created', 'Meta criada no Google Ads'));
        setNewGoalModalOpen(false);
        await loadGoogleSettings();
        // Abre o snippet da meta recém-criada pra o usuário copiar a tag.
        if (res.data?.id) {
          openSnippetModal(res.data.id, newGoalName.trim());
        }
      } else {
        toast.error(res.message || t('campaigns.googleNewGoal.error', 'Falha ao criar a meta'));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || t('campaigns.googleNewGoal.error', 'Falha ao criar a meta'));
    } finally {
      setCreatingGoal(false);
    }
  };

  // Meta pages + LinkedIn orgs — used to resolve the name/avatar of the publisher
  // selected per-ad in ad.settings.meta_page_id / linkedin_org_urn.
  const [metaPages, setMetaPages] = useState<Array<{
    page_id: string;
    page_name: string;
    page_picture?: string | null;
  }>>([]);
  const [linkedinOrgs, setLinkedinOrgs] = useState<Array<{
    organization_urn: string;
    name: string;
    logo_url?: string | null;
  }>>([]);

  // Search terms state
  const [searchTerms, setSearchTerms] = useState<SearchTermReport[]>([]);
  const [loadingSearchTerms, setLoadingSearchTerms] = useState(false);
  const [searchTermsPage, setSearchTermsPage] = useState(1);
  const [searchTermsTotal, setSearchTermsTotal] = useState(0);
  const SEARCH_TERMS_PER_PAGE = 25;

  // Match type picker for search terms
  const [matchTypePicker, setMatchTypePicker] = useState<{ termId: string; type: 'positive' | 'negative' } | null>(null);

  // Recommendations tab state (AI + Google/Meta native)
  const [analysisState, setAnalysisState] = useState<{
    loading: boolean;
    result: CampaignAnalysisResult | null;
    error: string | null;
    dismissed: Set<string>;
  }>({ loading: false, result: null, error: null, dismissed: new Set() });

  // Per-ad recommendations (triggered from the ad card "Analisar" button).
  // Results land inline in the "💡 Dicas" section below the ad card, keyed by adId.
  const [adAnalyzing, setAdAnalyzing] = useState<string | null>(null);
  const [adRecsByAdId, setAdRecsByAdId] = useState<Record<string, CampaignAnalysisResult>>({});

  const handleAnalyzeAd = async (ad: any) => {
    if (!id) return;
    setAdAnalyzing(ad.id);
    try {
      const { analyzeAd } = await import('../../services/campaignService');
      const result = await analyzeAd(id, ad.id);
      setAdRecsByAdId((prev) => ({ ...prev, [ad.id]: result }));
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao analisar anúncio');
    } finally {
      setAdAnalyzing(null);
    }
  };

  const handleAnalyze = async () => {
    if (!id) return;
    setAnalysisState((s) => ({ ...s, loading: true, error: null }));
    try {
      const result = await analyzeCampaignApi(id);
      setAnalysisState((s) => ({ ...s, loading: false, result }));
    } catch (err: any) {
      setAnalysisState((s) => ({ ...s, loading: false, error: err?.message || 'Erro na análise' }));
    }
  };

  const handleApplyGoogleRec = async (rec: RecommendationCard) => {
    if (!id || !rec.applyResourceName) return;
    try {
      await applyGoogleRecApi(id, rec.applyResourceName);
      setAnalysisState((s) => ({ ...s, dismissed: new Set([...Array.from(s.dismissed), rec.id]) }));
    } catch (err: any) {
      alert(err?.message || 'Falha ao aplicar recomendação');
    }
  };


  const [autoSynced, setAutoSynced] = useState(false);
  const autoSyncedRef = React.useRef(false);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState('');

  useEffect(() => {
    // Reset auto-sync flag and pagination when campaign ID changes
    autoSyncedRef.current = false;
    setAutoSynced(false);
    setSearchTermsPage(1);
    setInlinePlatformResults(null);
    if (id) {
      loadCampaign();
      loadPlatformStatus();
    }
  }, [id]);

  // Auto-sync campaign data from platforms when opening the page (once per campaign)
  useEffect(() => {
    // Campanha só do ChatGPT: syncSearchTerms/syncCampaignDataFromPlatform não
    // têm branch openai — rodar mostraria "Platform data updated" sem fazer nada
    const isOpenaiOnlyCampaign = !!campaign && (campaign.platforms?.length ? campaign.platforms : [campaign.platform]).every((p: string) => p === 'openai');
    if (id && campaign && !autoSyncedRef.current && !isOpenaiOnlyCampaign) {
      autoSyncedRef.current = true;
      setAutoSynced(true);
      setAutoSyncing(true);
      setSyncProgress(t('campaigns.sync.syncingData', 'Sincronizando dados das plataformas...'));
      // Sincroniza métricas (cliques, gasto, impressões) da plataforma + search terms
      // ao abrir a campanha — antes só puxava search terms, então campanha antiga/pausada
      // abria sem os detalhes completos (caso Panda).
      Promise.allSettled([
        syncCampaignMetricsApi(id),
        campaignService.syncSearchTerms(id),
      ]).then(() => {
        setSyncProgress(t('campaigns.sync.refreshingData', 'Refreshing data...'));
        return Promise.all([
          loadCampaign(),
          loadSearchTerms()
        ]);
      }).then(() => {
        toast.success(t('campaigns.sync.autoSyncDone', 'Platform data updated'));
      }).catch(() => {
        // Silent fail - user can manually sync
      }).finally(() => {
        setAutoSyncing(false);
        setSyncProgress('');
      });
    }
  }, [id, campaign]);

  useEffect(() => {
    if (id && activeTab === 'search-terms') {
      loadSearchTerms();
    }
  }, [id, activeTab, searchTermsPage]);

  useEffect(() => {
    if (id && activeTab === 'ads') {
      loadAds();
    }
  }, [id, activeTab]);

  // Load the FB pages / LinkedIn orgs lists once we know the campaign's
  // platforms, so we can resolve each ad's publisher name/avatar in the preview
  // and in the ads list.
  useEffect(() => {
    if (!campaign) return;
    const platforms: string[] = campaign.platforms || (campaign.platform ? [campaign.platform] : []);
    if (platforms.includes('meta') && metaPages.length === 0) {
      campaignService.getMetaInstagramAccounts().then(res => {
        if (res.success && Array.isArray(res.data)) {
          setMetaPages(res.data.filter((p: any) => p?.page_id));
        }
      }).catch(() => {});
    }
    if (platforms.includes('linkedin') && linkedinOrgs.length === 0) {
      campaignService.getLinkedinOrganizations().then(res => {
        if (res.success && Array.isArray(res.data)) {
          setLinkedinOrgs(res.data);
        }
      }).catch(() => {});
    }
  }, [campaign]);

  // Busca a configuração ao vivo do Google Ads (estratégia + metas) uma vez,
  // quando a campanha for Google.
  useEffect(() => {
    if (!campaign || !id) return;
    const isGoogle = !!(
      campaign.external_id_google ||
      campaign.platforms?.includes('google') ||
      campaign.platform === 'google' ||
      campaign.platform === 'both'
    );
    if (isGoogle) {
      loadGoogleSettings();
    }
  }, [campaign, id]);

  const loadCampaign = async () => {
    try {
      const [campaignRes, metricsRes] = await Promise.all([
        campaignService.getCampaign(id!),
        campaignService.getCampaignMetrics(id!)
      ]);

      if (campaignRes.success) {
        setCampaign(campaignRes.data!);
        // Hydrate persisted campaign-level recommendations so the "Recomendações"
        // tab loads pre-filled (no re-run until the user explicitly clicks again).
        const persisted = (campaignRes.data as any)?.settings?.lastAnalysis;
        if (persisted) {
          setAnalysisState((s) => ({ ...s, result: persisted }));
        }
      }
      if (metricsRes.success) {
        setMetrics(metricsRes.data);
      }
    } catch (error) {
      console.error('Error loading campaign:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSearchTerms = async () => {
    setLoadingSearchTerms(true);
    try {
      const response = await campaignService.getCampaignSearchTerms(id!, { limit: SEARCH_TERMS_PER_PAGE, page: searchTermsPage });
      if (response.success) {
        setSearchTerms(response.data || []);
        setSearchTermsTotal(response.pagination?.total || response.data?.length || 0);
      }
    } catch (error) {
      console.error('Error loading search terms:', error);
    } finally {
      setLoadingSearchTerms(false);
    }
  };

  const loadPlatformStatus = async () => {
    if (!id) return;
    setLoadingPlatformStatus(true);
    try {
      const response = await campaignService.getCampaignPlatformStatus(id);
      if (response.success && response.data) {
        setPlatformStatus(response.data);
      }
    } catch (error) {
      console.error('Error loading platform status:', error);
    } finally {
      setLoadingPlatformStatus(false);
    }
  };

  const loadGoogleSettings = async () => {
    if (!id) return;
    setLoadingGoogleSettings(true);
    try {
      const res = await campaignService.getGoogleSettings(id);
      if (res.success && res.data) {
        setGoogleSettings(res.data);
        setGoogleSettingsCode(null);
      } else {
        setGoogleSettings(null);
        setGoogleSettingsCode(res.code || null);
      }
    } catch (error) {
      console.error('Error loading Google settings:', error);
      setGoogleSettings(null);
      setGoogleSettingsCode(null);
    } finally {
      setLoadingGoogleSettings(false);
    }
  };

  // Mapeia o enum do Google (biddingStrategyType) pra nossas chaves de estratégia.
  const mapGoogleStrategyToKey = (type: string | null | undefined): string => {
    switch ((type || '').toUpperCase()) {
      case 'TARGET_SPEND': return 'maximize_clicks';
      case 'MAXIMIZE_CONVERSIONS': return 'maximize_conversions';
      case 'TARGET_CPA': return 'target_cpa';
      case 'MAXIMIZE_CONVERSION_VALUE': return 'maximize_conversion_value';
      case 'TARGET_ROAS': return 'target_roas';
      case 'MANUAL_CPC':
      case 'MANUAL_CPM': return 'manual_cpc';
      default: return 'maximize_clicks';
    }
  };

  // Portfólio que a campanha usa hoje: vem do Google quando o settings expõe, senão do que gravamos ao aplicar.
  const currentPortfolioRn: string | null =
    (googleSettings as any)?.biddingStrategy || (googleSettings as any)?.bidding_strategy ||
    (campaign as any)?.settings?.google_portfolio_strategy || null;
  const currentPortfolio = currentPortfolioRn ? portfolios.find((p) => p.resource_name === currentPortfolioRn) || null : null;

  const startEditingBid = () => {
    if (!googleSettings) return;
    setBidMode(currentPortfolioRn ? 'portfolio' : 'standard');
    setBidStrategy(mapGoogleStrategyToKey(googleSettings.biddingStrategyType));
    setBidTargetCpa(googleSettings.targetCpa != null ? String(googleSettings.targetCpa) : '');
    setBidTargetRoas(googleSettings.targetRoas != null ? String(googleSettings.targetRoas * 100) : '');
    setEditingBid(true);
  };

  // BRL currency mask: digits typed are read as cents (e.g. "61173" -> "611,73", "6117300" -> "61.173,00").
  const maskBRL = (raw: string) => {
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return '';
    return (parseInt(digits, 10) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const parseBRL = (s: string) => {
    const digits = String(s).replace(/\D/g, '');
    return digits ? parseInt(digits, 10) / 100 : NaN;
  };

  const handleSaveBidding = async () => {
    if (!id) return;
    if (!window.confirm(t('campaigns.googleBidding.confirm', 'Isso altera a estratégia de lance direto no Google Ads. Continuar?'))) {
      return;
    }
    const payload: { strategy: string; targetCpa?: number | null; targetRoas?: number | null } = { strategy: bidStrategy };
    if (bidStrategy === 'target_cpa' || bidStrategy === 'maximize_conversions') {
      const cpa = parseBRL(bidTargetCpa);
      if (!isNaN(cpa)) payload.targetCpa = cpa;
    }
    if (bidStrategy === 'target_roas' || bidStrategy === 'maximize_conversion_value') {
      const roasPct = parseFloat(bidTargetRoas.replace(',', '.'));
      if (!isNaN(roasPct)) payload.targetRoas = roasPct / 100;
    }
    setSavingBid(true);
    try {
      const res = await campaignService.updateGoogleBidding(id, payload);
      if (res.success) {
        toast.success(t('campaigns.googleBidding.saved', 'Estratégia atualizada no Google Ads'));
        setEditingBid(false);
        // Saiu do portfólio: recarrega a campanha (settings.google_portfolio_strategy zera) — o efeito recarrega o Google.
        if (currentPortfolioRn) await loadCampaign();
        else await loadGoogleSettings();
        reloadPortfolios();
      } else {
        const msg = res.message || '';
        if (msg.includes('OPERATION_NOT_PERMITTED') || res.code === 'OPERATION_NOT_PERMITTED') {
          toast.error(t('campaigns.googleBidding.notPermitted', 'O Google não permite essa estratégia nesta campanha agora (ex.: falta histórico de conversões).'));
        } else {
          toast.error(msg || t('campaigns.googleBidding.error', 'Falha ao atualizar a estratégia'));
        }
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '';
      const code = err?.response?.data?.code;
      if (msg.includes('OPERATION_NOT_PERMITTED') || code === 'OPERATION_NOT_PERMITTED') {
        toast.error(t('campaigns.googleBidding.notPermitted', 'O Google não permite essa estratégia nesta campanha agora (ex.: falta histórico de conversões).'));
      } else {
        toast.error(msg || t('campaigns.googleBidding.error', 'Falha ao atualizar a estratégia'));
      }
    } finally {
      setSavingBid(false);
    }
  };

  // Agrupa as metas de conversão por categoria (uma categoria conta para o lance
  // quando QUALQUER meta dentro dela é biddable).
  const getGoalCategoryGroups = () => {
    const groups: Record<string, { categoryLabel: string; names: string[]; goals: GoogleCampaignSettings['conversionGoals']; active: boolean }> = {};
    for (const goal of googleSettings?.conversionGoals || []) {
      const key = goal.category;
      if (!groups[key]) {
        groups[key] = { categoryLabel: goal.categoryLabel, names: [], goals: [], active: false };
      }
      groups[key].names.push(goal.name);
      groups[key].goals.push(goal);
      if (goal.biddable) groups[key].active = true;
    }
    return groups;
  };

  const startEditingGoals = () => {
    const groups = getGoalCategoryGroups();
    const initial: Record<string, boolean> = {};
    Object.keys(groups).forEach((cat) => {
      initial[cat] = groups[cat].active;
    });
    setGoalCats(initial);
    setEditingGoals(true);
  };

  const handleSaveGoals = async () => {
    if (!id) return;
    if (!window.confirm(t('campaigns.googleGoals.confirm', 'Isso altera quais metas contam para o lance, direto no Google Ads. Continuar?'))) {
      return;
    }
    const selectedCategories = Object.keys(goalCats).filter((cat) => goalCats[cat]);
    if (selectedCategories.length === 0) {
      toast.error(t('campaigns.googleGoals.needOne', 'Selecione pelo menos uma meta.'));
      return;
    }
    setSavingGoals(true);
    try {
      const res = await campaignService.updateGoogleConversionGoals(id, selectedCategories);
      if (res.success) {
        toast.success(t('campaigns.googleGoals.saved', 'Metas atualizadas no Google Ads'));
        await loadGoogleSettings();
        setEditingGoals(false);
      } else {
        toast.error(res.message || t('campaigns.googleGoals.error', 'Falha ao atualizar as metas'));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '';
      toast.error(msg || t('campaigns.googleGoals.error', 'Falha ao atualizar as metas'));
    } finally {
      setSavingGoals(false);
    }
  };

  const loadAds = async () => {
    setLoadingAds(true);
    try {
      const response = await adService.getAds(id!, { limit: 50 });
      if (response.success) {
        const list = response.data || [];
        setAds(list);
        // Hydrate persisted per-ad analyses so the "Dicas" section shows the
        // stored recommendations without the user having to click "Analisar"
        // again. Refresh only happens on explicit click.
        const hydrated: Record<string, CampaignAnalysisResult> = {};
        list.forEach((ad: any) => {
          const last = ad?.settings?.lastAnalysis;
          if (last) hydrated[ad.id] = last;
        });
        if (Object.keys(hydrated).length > 0) {
          setAdRecsByAdId((prev) => ({ ...hydrated, ...prev }));
        }
      }
    } catch (error) {
      console.error('Error loading ads:', error);
    } finally {
      setLoadingAds(false);
    }
  };

  const handleToggleAdStatus = async (ad: Ad) => {
    const newStatus = ad.status === 'active' ? 'paused' : 'active';
    setTogglingAdIds(prev => new Set(prev).add(ad.id));
    try {
      const projectId = currentProject?.id || '';
      const result = await adService.toggleAdStatus(projectId, ad.id, newStatus);
      if (result.success) {
        const hasWarnings = result.data?.platformWarnings?.length > 0;
        if (hasWarnings) {
          toast.success(
            newStatus === 'active'
              ? t('ads.toasts.activatedWithWarning', 'Ad activated locally. Platform sync pending.')
              : t('ads.toasts.pausedWithWarning', 'Ad paused locally. Platform sync pending.'),
            { duration: 4000 }
          );
        } else {
          toast.success(
            newStatus === 'active'
              ? t('ads.toasts.activated', 'Ad activated')
              : t('ads.toasts.paused', 'Ad paused')
          );
        }
        // Optimistic update: immediately reflect the new status in local state
        // so the button shows the correct action if clicked again before reload completes
        setAds(prev => prev.map(a => a.id === ad.id ? { ...a, status: newStatus } : a));
        loadAds();
        loadCampaign();
      } else {
        toast.error(result.message || t('ads.toasts.error', 'Failed to update ad'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('ads.toasts.error'));
    } finally {
      setTogglingAdIds(prev => {
        const next = new Set(prev);
        next.delete(ad.id);
        return next;
      });
    }
  };

  // Remove o anúncio na plataforma (Google/Meta). Não dá pra desfazer.
  const handleRemoveAdFromPlatform = async (ad: Ad) => {
    const where = [ad.external_id_google && 'Google Ads', ad.external_id_meta && 'Meta'].filter(Boolean).join(' e ');
    if (!window.confirm(t('campaigns.rmf.adRemoveConfirm', 'Remover o anúncio "{{name}}" do {{where}}? Ele é apagado na plataforma e isso não pode ser desfeito. Se só quer parar de veicular, use Pausar.', { name: ad.name, where }))) return;
    setRemovingAdIds(prev => new Set(prev).add(ad.id));
    try {
      const res = await api.post(`/ads/${ad.id}/remove-from-platform`);
      if (res.data?.success) {
        toast.success(res.data?.message || t('campaigns.rmf.adRemoved', 'Anúncio removido da plataforma'));
      } else {
        toast.error(res.data?.message || t('campaigns.rmf.adRemoveError', 'Falha ao remover o anúncio'));
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('campaigns.rmf.adRemoveError', 'Falha ao remover o anúncio'));
    } finally {
      setRemovingAdIds(prev => {
        const next = new Set(prev);
        next.delete(ad.id);
        return next;
      });
      loadAds();
    }
  };

  const [syncing, setSyncing] = useState(false);

  const handleSyncAll = async () => {
    if (!id) return;
    setSyncing(true);
    setSyncProgress(t('campaigns.sync.syncingMetrics', 'Buscando métricas das plataformas (Google, Meta, LinkedIn)...') as string);
    try {
      // Run both in parallel: search terms (Google) + performance metrics (all platforms)
      const [metricsResult] = await Promise.all([
        syncCampaignMetricsApi(id).catch((e) => ({ errors: [e?.message || 'metrics error'] } as any)),
        campaignService.syncSearchTerms(id).catch(() => null)
      ]);

      const errs = (metricsResult as any)?.errors || [];
      if (errs.length > 0) {
        toast.error(`Algumas fontes falharam: ${errs.slice(0, 2).join(', ')}`);
      } else {
        toast.success(t('campaigns.sync.success', 'Dados atualizados'));
      }

      // Reload everything that depends on metrics/search terms
      const reloadPromises: Promise<void>[] = [loadCampaign(), loadSearchTerms()];
      if (activeTab === 'ads') reloadPromises.push(loadAds());
      await Promise.all(reloadPromises);
    } catch (error: any) {
      const msg = error.response?.data?.message || t('errors.generic', 'Error syncing');
      toast.error(msg);
    } finally {
      setSyncing(false);
      setSyncProgress('');
    }
  };

  // Export relatório (PDF ou XLSX) — chama o backend, recebe blob e dispara download
  const [exporting, setExporting] = useState<false | 'pdf' | 'xlsx'>(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportReport = async (format: 'pdf' | 'xlsx') => {
    if (!id) return;
    setExportMenuOpen(false);
    setExporting(format);
    const toastId = toast.loading(
      format === 'pdf'
        ? t('campaigns.export.generatingPdf', 'Gerando PDF do relatório...')
        : t('campaigns.export.generatingXlsx', 'Gerando Excel do relatório...')
    );
    try {
      const resp = await api.get(`/campaigns/${id}/export`, {
        params: { format },
        responseType: 'blob',
        timeout: 60000,
      });
      const blob = new Blob([resp.data], {
        type: format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      // Extrai filename do Content-Disposition, fallback pra nome genérico
      const cd = resp.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `relatorio_campanha.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(
        t('campaigns.export.success', 'Relatório gerado com sucesso!'),
        { id: toastId }
      );
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Erro';
      toast.error(
        t('campaigns.export.error', 'Erro ao gerar relatório: {{msg}}', { msg }),
        { id: toastId, duration: 5000 }
      );
    } finally {
      setExporting(false);
    }
  };

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    if (!exportMenuOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-export-menu]')) setExportMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportMenuOpen]);

  const handleToggleStatus = async () => {
    if (!campaign) return;
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    setIsToggling(true);

    const toastId = toast.loading(
      newStatus === 'active'
        ? t('campaigns.toasts.activating', 'Activating campaign on platforms...')
        : t('campaigns.toasts.pausing', 'Pausing campaign on platforms...'),
      { id: `toggle-detail-${campaign.id}` }
    );

    try {
      const projectId = currentProject?.id || '';
      const result = await campaignService.togglePlatformStatus(projectId, campaign.id, newStatus);
      const platformResults = result.data?.platformDetails || result.data?.platforms || {};
      setInlinePlatformResults(platformResults);

      const hasErrors = Object.values(platformResults).some((r: any) => !r.skipped && !r.success);
      const localUpdated = result.data?.localUpdated !== false;

      if (hasErrors && !localUpdated) {
        toast.error(
          t('campaigns.toasts.platformSyncFailed', 'Platform did not confirm the status change. Campaign status was not changed.'),
          { id: toastId, duration: 5000 }
        );
      } else if (hasErrors) {
        toast.error(t('campaigns.toggleResult.hasErrors'), { id: toastId, duration: 4000 });
      } else {
        toast.success(
          result.message || (newStatus === 'active' ? t('campaigns.toasts.activatedOnPlatform') : t('campaigns.toasts.pausedOnPlatform')),
          { id: toastId, duration: 3000 }
        );
      }

      loadCampaign();
      if (activeTab === 'ads') loadAds();
    } catch (error: any) {
      toast.error(
        t('campaigns.toasts.platformError', 'Failed to update campaign on platform. Status was not changed.'),
        { id: toastId, duration: 5000 }
      );
      loadCampaign();
      if (activeTab === 'ads') loadAds();
    } finally {
      setIsToggling(false);
    }
  };

  const handlePushToPlatform = async (platform: 'google' | 'meta' | 'linkedin') => {
    if (!campaign) return;
    const projectId = currentProject?.id || '';
    setPushingPlatform(platform);

    const baseMsg = t('campaigns.toggleResult.pushing', 'Enviando campanha...');
    const toastId = toast.loading(baseMsg, { id: `push-${platform}` });

    const onProgress = (progress: number, status: string) => {
      const label = status === 'active' ? 'Processando' : status === 'waiting' ? 'Aguardando' : 'Sincronizando';
      toast.loading(`${baseMsg} ${label} ${progress}%`, { id: toastId });
    };

    try {
      let result;
      if (platform === 'google') result = await campaignService.pushToGoogleAds(projectId, campaign.id, onProgress);
      else if (platform === 'meta') result = await campaignService.pushToMetaAds(projectId, campaign.id, onProgress);
      else result = await campaignService.pushToLinkedIn(projectId, campaign.id, onProgress);

      // Update inline result for this platform
      const responseData = result?.data as any;
      const adErrors: string[] = responseData?.data?.adErrors || responseData?.adErrors || [];
      const pushSuccess = responseData?.success !== false;
      const adSetFailed = responseData?.data?.adSetFailed || false;

      setInlinePlatformResults(prev => ({
        ...(prev || {}),
        [platform]: { success: pushSuccess, action: pushSuccess ? 'created' : undefined, error: !pushSuccess ? (responseData?.message || adErrors[0]) : undefined },
      }));

      if (adSetFailed) {
        // Ad set creation failed - campaign is incomplete on platform
        toast.error(
          responseData?.message || 'Campanha criada, mas o conjunto de anúncios falhou. Tente novamente.',
          { id: toastId, duration: 8000 }
        );
      } else if (adErrors.length > 0) {
        // Partial success - campaign created but some ads had errors
        toast.success(
          responseData?.message || `Campanha criada, mas ${adErrors.length} erro(s) nos anúncios`,
          { id: toastId, duration: 6000 }
        );
      } else {
        toast.success(t('campaigns.toggleResult.success'), { id: toastId, duration: 3000 });
      }
      loadCampaign();
    } catch (error: any) {
      const errMsg = error.response?.data?.message || error.response?.data?.error || error.message || t('campaigns.toggleResult.error');
      setInlinePlatformResults(prev => ({
        ...(prev || {}),
        [platform]: { success: false, error: errMsg },
      }));
      toast.error(errMsg, { id: toastId, duration: 5000 });
    } finally {
      setPushingPlatform(null);
    }
  };

  const handleAddAsKeyword = async (term: SearchTermReport, type: 'positive' | 'negative', matchType: 'exact' | 'phrase' | 'broad') => {
    try {
      setMatchTypePicker(null);
      await campaignService.addSearchTermAsKeyword(id!, {
        searchTermId: term.id,
        searchTerm: term.search_term,
        type,
        matchType
      });
      const matchLabel = t(`campaigns.campaignKeywords.${matchType}`, matchType);
      toast.success(
        type === 'positive'
          ? t('campaigns.searchTerms.addedAsKeyword', '"{{term}}" adicionada como keyword ({{matchType}})', { term: term.search_term, matchType: matchLabel })
          : t('campaigns.searchTerms.addedAsNegative', '"{{term}}" adicionada como negativa ({{matchType}})', { term: term.search_term, matchType: matchLabel })
      );
      setSearchTerms(prev => prev.map(t =>
        t.id === term.id
          ? { ...t, added_as_keyword: type === 'positive', added_as_negative: type === 'negative' }
          : t
      ));
    } catch (error) {
      toast.error('Error adding keyword');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'gray' | 'primary'> = {
      active: 'success',
      paused: 'warning',
      draft: 'gray',
      completed: 'primary',
      archived: 'gray'
    };
    return variants[status] || 'gray';
  };

  const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  const formatPercent = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="h-64 skeleton rounded-xl" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('campaigns.not_found')}</p>
        <Link to="/campaigns">
          <Button className="mt-4">{t('common.back')}</Button>
        </Link>
      </div>
    );
  }

  // Use synced metrics when available, fallback to campaign totals
  const displayImpressions = metrics?.impressions ?? campaign.total_impressions ?? 0;
  const displayClicks = metrics?.clicks ?? campaign.total_clicks ?? 0;
  const displaySpend = metrics?.spend ?? campaign.total_spent ?? 0;
  const displayConversions = metrics?.conversions ?? campaign.total_conversions ?? 0;

  const statCards = [
    {
      title: t('campaigns.metrics.impressions'),
      value: Number(displayImpressions).toLocaleString('pt-BR'),
      icon: Eye,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30'
    },
    {
      title: t('campaigns.metrics.clicks'),
      value: Number(displayClicks).toLocaleString('pt-BR'),
      icon: MousePointer,
      color: 'text-green-600',
      bgColor: 'bg-green-100 dark:bg-green-900/30'
    },
    {
      title: t('campaigns.metrics.spend'),
      value: formatCurrency(Number(displaySpend) || 0),
      icon: DollarSign,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30'
    },
    {
      title: t('campaigns.metrics.conversions'),
      value: Number(displayConversions).toLocaleString('pt-BR'),
      icon: Target,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30'
    }
  ];

  // Keywords + Search Terms são conceitos exclusivos do Google Ads (Meta/LinkedIn não têm keyword).
  // Exibe essas abas apenas quando a campanha tem Google Ads conectado.
  const hasGoogle = !!(campaign?.external_id_google || campaign?.platforms?.includes('google') || campaign?.platform === 'google' || campaign?.platform === 'both');

  const tabs = [
    { key: 'overview' as const, label: t('campaigns.tabs.overview', 'Overview'), icon: <Eye className="w-4 h-4" /> },
    { key: 'ads' as const, label: t('campaigns.tabs.ads', 'Anuncios'), icon: <Megaphone className="w-4 h-4" /> },
    { key: 'recommendations' as const, label: t('campaigns.tabs.recommendations', 'Recomendações'), icon: <Sparkles className="w-4 h-4" /> },
    ...(hasGoogle ? [
      { key: 'keywords' as const, label: t('campaigns.tabs.keywords', 'Keywords'), icon: <Target className="w-4 h-4" /> },
      { key: 'search-terms' as const, label: t('campaigns.tabs.searchTerms', 'Search Terms'), icon: <Search className="w-4 h-4" /> },
    ] : []),
    { key: 'automation' as const, label: t('campaigns.tabs.automation', 'Automation'), icon: <Zap className="w-4 h-4" /> },
    { key: 'analytics' as const, label: t('campaigns.tabs.analytics', 'Analytics'), icon: <BarChart3 className="w-4 h-4" /> },
    { key: 'media' as const, label: t('campaigns.tabs.media', 'Mídia'), icon: <Palette className="w-4 h-4" /> }
  ];

  return (
    <div className="space-y-4 sm:space-y-6 min-w-0 max-w-full overflow-x-clip">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
        <div className="flex items-start sm:items-center gap-2 sm:gap-4 min-w-0">
          <Link to="/campaigns" className="p-2 -ml-2 sm:ml-0 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg flex-shrink-0" aria-label={t('common.back', 'Voltar')}>
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="min-w-0">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white break-words min-w-0 leading-tight">{campaign.name}</h1>
              <Badge variant={getStatusBadge(campaign.status)}>
                {t(`campaigns.status.${campaign.status}`)}
              </Badge>
            </div>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 capitalize break-words">
              {campaign.platform === 'openai' ? 'ChatGPT Ads' : campaign.platform} • {objetivoLabel(campaign.objective)}
            </p>
          </div>
        </div>
        {/* Ações: no celular, principal com texto curto e secundárias só ícone, numa linha só */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant={campaign.status === 'active' ? 'secondary' : 'primary'}
            onClick={handleToggleStatus}
            disabled={isToggling}
            className="flex-1 sm:flex-none whitespace-nowrap"
            leftIcon={
              isToggling
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : campaign.status === 'active'
                  ? <Pause className="w-4 h-4" />
                  : <Play className="w-4 h-4" />
            }
          >
            {campaign.status === 'active' ? t('campaigns.actions.pause') : t('campaigns.actions.activate')}
          </Button>
          <Button
            variant="secondary"
            onClick={handleSyncAll}
            disabled={syncing || autoSyncing}
            className="whitespace-nowrap"
            aria-label={t('campaigns.sync.button', 'Sincronizar')}
            title={t('campaigns.sync.button', 'Sincronizar')}
            leftIcon={<RefreshCw className={`w-4 h-4 ${(syncing || autoSyncing) ? 'animate-spin' : ''}`} />}
          >
            <span className="hidden sm:inline">{t('campaigns.sync.button', 'Sincronizar')}</span>
          </Button>
          <div className="relative" data-export-menu>
            <Button
              variant="secondary"
              onClick={() => setExportMenuOpen(v => !v)}
              disabled={!!exporting}
              className="whitespace-nowrap"
              aria-label={t('campaigns.export.button', 'Exportar')}
              title={t('campaigns.export.button', 'Exportar')}
              leftIcon={
                exporting
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Download className="w-4 h-4" />
              }
              rightIcon={!exporting ? <ChevronDown className="w-3.5 h-3.5 hidden sm:block" /> : undefined}
            >
              <span className="hidden sm:inline">
                {exporting === 'pdf'
                  ? t('campaigns.export.exportingPdf', 'Exportando PDF...')
                  : exporting === 'xlsx'
                    ? t('campaigns.export.exportingXlsx', 'Exportando Excel...')
                    : t('campaigns.export.button', 'Exportar')}
              </span>
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-56 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-30 overflow-hidden">
                <button
                  type="button"
                  onClick={() => exportReport('pdf')}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left border-b border-gray-100 dark:border-gray-700/50"
                >
                  <div className="w-9 h-9 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4.5 h-4.5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('campaigns.export.pdfLabel', 'Relatório PDF')}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('campaigns.export.pdfHint', 'Documento completo com gráfico, KPIs e tabelas')}
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => exportReport('xlsx')}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left"
                >
                  <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <FileSpreadsheet className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('campaigns.export.xlsxLabel', 'Planilha Excel')}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('campaigns.export.xlsxHint', '6 abas com dados brutos pra análise')}
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
          {/* Editor de campanha ChatGPT é fase 2 — o wizard travaria numa aba de criativo vazia */}
          {!(campaign.platforms?.length ? campaign.platforms : [campaign.platform]).every((p: string) => p === 'openai') && (
          <Link to={`/campaigns/${id}/edit`} className="flex-1 sm:flex-none">
            <Button leftIcon={<Edit className="w-4 h-4" />} className="w-full whitespace-nowrap">
              {t('common.edit')}
            </Button>
          </Link>
          )}
        </div>
      </div>

      {(syncing || autoSyncing) && (
        <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2.5 animate-in fade-in">
          <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin flex-shrink-0" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            {syncProgress || t('campaigns.sync.syncHint', 'Buscando keywords, termos de busca, cliques e métricas das plataformas')}
          </p>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} padding={false} className="p-3 sm:p-6 min-w-0">
              <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                <div className={`w-9 h-9 sm:w-12 sm:h-12 ${stat.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-4 h-4 sm:w-6 sm:h-6 ${stat.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-base sm:text-2xl font-bold text-gray-900 dark:text-white truncate" title={stat.value}>{stat.value}</p>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">{stat.title}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Abas — celular: botões que quebram linha (sem rolagem lateral); desktop: abas sublinhadas */}
      <div className="sm:border-b border-gray-200 dark:border-gray-700">
        <nav className="flex flex-wrap gap-1.5 sm:gap-x-1 sm:gap-y-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 lg:px-4 py-2 sm:py-3 rounded-full sm:rounded-none border sm:border-0 sm:border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-primary-50 border-primary-300 sm:bg-transparent sm:border-primary-500 text-primary-600 dark:bg-primary-900/30 dark:border-primary-700 sm:dark:bg-transparent sm:dark:border-primary-500 dark:text-primary-400'
                  : 'bg-white border-gray-200 sm:bg-transparent sm:border-transparent dark:bg-gray-800 dark:border-gray-700 sm:dark:bg-transparent sm:dark:border-transparent text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Quick Action Cards */}
          <div className={`grid grid-cols-1 ${hasGoogle ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-2 sm:gap-4`}>
            {hasGoogle && (
            <button onClick={() => setActiveTab('keywords')} className="p-3 sm:p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-300 dark:hover:border-primary-700 transition-all text-left group min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <Target className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white">{t('campaigns.tabs.keywords', 'Keywords')}</p>
                  <p className="text-xs text-gray-500">{t('campaigns.quickActions.manageKeywords', 'Manage keywords and negatives')}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:text-primary-500 transition-colors" />
              </div>
            </button>
            )}
            <button onClick={() => setActiveTab('automation')} className="p-3 sm:p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-300 dark:hover:border-primary-700 transition-all text-left group min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <Zap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white">{t('campaigns.tabs.automation', 'Automation')}</p>
                  <p className="text-xs text-gray-500">{t('campaigns.quickActions.automationRules', 'Manage automation rules')}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:text-primary-500 transition-colors" />
              </div>
            </button>
            <button onClick={() => setActiveTab('analytics')} className="p-3 sm:p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-primary-300 dark:hover:border-primary-700 transition-all text-left group min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white">{t('campaigns.tabs.analytics', 'Analytics')}</p>
                  <p className="text-xs text-gray-500">{t('campaigns.quickActions.viewAnalytics', 'View detailed analytics')}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:text-primary-500 transition-colors" />
              </div>
            </button>
          </div>

          {/* Metas & Estratégia (Google) — config ao vivo da campanha no Google Ads */}
          {hasGoogle && (
            <Card padding={false} className="p-4 sm:p-6">
              <div className="mb-4">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                    <Target className="w-5 h-5 flex-shrink-0" />
                    {t('campaigns.googleSettings.title', 'Metas & Estratégia (Google)')}
                  </CardTitle>
                  <button
                    onClick={() => loadGoogleSettings()}
                    disabled={loadingGoogleSettings}
                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 disabled:opacity-50"
                    title={t('campaigns.googleSettings.refresh', 'Atualizar')}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingGoogleSettings ? 'animate-spin' : ''}`} />
                    {t('campaigns.googleSettings.refresh', 'Atualizar')}
                  </button>
                </div>
              </div>
              <CardContent>
                {loadingGoogleSettings && !googleSettings ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('campaigns.googleSettings.loading', 'Carregando configuração do Google Ads...')}
                  </div>
                ) : (googleSettingsCode === 'GOOGLE_ADS_NOT_CONNECTED' || googleSettingsCode === 'GOOGLE_ADS_NEEDS_RECONNECT') ? (
                  <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2.5">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{t('campaigns.googleSettings.connect', 'Conecte/reconecte o Google Ads para ver as metas e a estratégia.')}</span>
                  </div>
                ) : googleSettings ? (
                  <div className="space-y-5">
                    {campaign?.id && <GoogleAiMaxToggle campaignId={campaign.id} />}
                    {/* Estratégia de lance */}
                    <div>
                      <div className="flex items-center flex-wrap gap-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t('campaigns.googleSettings.bidStrategy', 'Estratégia de lance')}:
                        </span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          {googleSettings.biddingStrategyType ? biddingTypeLabel(t, googleSettings.biddingStrategyType) : (googleSettings.biddingStrategyLabel || '—')}
                        </span>
                        {currentPortfolioRn ? (
                          <Badge variant="primary">
                            {t('campaigns.rmf.usesPortfolio', 'Portfólio: {{name}}', { name: currentPortfolio?.name || (loadingPortfolios ? '…' : String(currentPortfolioRn).split('/').pop()) })}
                          </Badge>
                        ) : (
                          <Badge variant="gray">{t('campaigns.rmf.usesStandard', 'Estratégia padrão')}</Badge>
                        )}
                        {googleSettings.aiBidding && (
                          <Badge variant="success">
                            <span className="flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              {t('campaigns.googleSettings.aiOn', 'IA ativa')}
                            </span>
                          </Badge>
                        )}
                        {!editingBid && (
                          <button
                            onClick={startEditingBid}
                            className="ml-auto text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                            title={t('campaigns.googleBidding.edit', 'Editar')}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            {t('campaigns.googleBidding.edit', 'Editar')}
                          </button>
                        )}
                      </div>

                      {/* Edição inline da estratégia de lance */}
                      {editingBid && (
                        <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-3">
                          {/* Padrão (só desta campanha) x portfólio (compartilhada) */}
                          <div className="flex flex-col sm:flex-row gap-2">
                            {([
                              { v: 'standard' as const, label: t('campaigns.rmf.modeStandard', 'Estratégia padrão (só desta campanha)') },
                              { v: 'portfolio' as const, label: t('campaigns.rmf.modePortfolio', 'Estratégia de portfólio (compartilhada)') },
                            ]).map((o) => (
                              <label
                                key={o.v}
                                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                                  bidMode === o.v
                                    ? 'border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-900/20 dark:text-primary-200'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="bid-mode"
                                  checked={bidMode === o.v}
                                  onChange={() => setBidMode(o.v)}
                                  disabled={savingBid}
                                  className="text-primary-600"
                                />
                                {o.label}
                              </label>
                            ))}
                          </div>
                          {bidMode === 'portfolio' && id ? (
                            <GooglePortfolioBidding
                              campaignId={id}
                              currentPortfolio={currentPortfolioRn}
                              portfolios={portfolios}
                              loading={loadingPortfolios}
                              reload={reloadPortfolios}
                              onAssigned={() => { setEditingBid(false); reloadPortfolios(); loadCampaign(); }}
                              onCancel={() => setEditingBid(false)}
                            />
                          ) : (<>
                          <Select
                            label={t('campaigns.googleBidding.title', 'Estratégia de lance')}
                            value={bidStrategy}
                            onChange={(e) => setBidStrategy(e.target.value)}
                            disabled={savingBid}
                            options={[
                              { value: 'maximize_clicks', label: t('campaigns.googleBidding.optMaxClicks', 'Maximizar cliques') },
                              { value: 'maximize_conversions', label: t('campaigns.googleBidding.optMaxConversions', 'Maximizar conversões') },
                              { value: 'target_cpa', label: t('campaigns.googleBidding.optTargetCpa', 'CPA alvo') },
                              { value: 'maximize_conversion_value', label: t('campaigns.googleBidding.optMaxConvValue', 'Maximizar valor de conversão') },
                              { value: 'target_roas', label: t('campaigns.rmf.optTargetRoas', 'ROAS alvo (Maximizar valor de conversão com ROAS)') },
                              { value: 'manual_cpc', label: t('campaigns.googleBidding.optManualCpc', 'CPC manual') },
                            ]}
                          />
                          {(bidStrategy === 'target_cpa' || bidStrategy === 'maximize_conversions') && (
                            <div>
                              <Input
                                type="text"
                                inputMode="decimal"
                                leftIcon={<span className="text-gray-400 text-sm font-medium">R$</span>}
                                placeholder="0,00"
                                label={t('campaigns.googleBidding.cpaLabel', 'CPA alvo (R$)')}
                                value={bidTargetCpa}
                                onChange={(e) => setBidTargetCpa(maskBRL(e.target.value))}
                                disabled={savingBid}
                              />
                              {googleSettings.avgCpa != null && googleSettings.avgCpa > 0 && (
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  {t('campaigns.googleBidding.cpaSuggestion', 'CPA médio atual (Google): R$ {{v}}', { v: googleSettings.avgCpa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) })}
                                  {' · '}
                                  <button
                                    type="button"
                                    className="text-primary-600 dark:text-primary-400 hover:underline"
                                    onClick={() => setBidTargetCpa(maskBRL(String(Math.round((googleSettings.avgCpa || 0) * 100))))}
                                  >
                                    {t('campaigns.googleBidding.useValue', 'usar')}
                                  </button>
                                </p>
                              )}
                            </div>
                          )}
                          {(bidStrategy === 'target_roas' || bidStrategy === 'maximize_conversion_value') && (
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              label={t('campaigns.googleBidding.roasLabel', 'ROAS alvo (%)')}
                              value={bidTargetRoas}
                              onChange={(e) => setBidTargetRoas(e.target.value)}
                              disabled={savingBid}
                            />
                          )}
                          <div className="flex items-center gap-2 pt-1">
                            <Button size="sm" onClick={handleSaveBidding} isLoading={savingBid} disabled={savingBid}>
                              {t('campaigns.googleBidding.save', 'Salvar')}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => setEditingBid(false)} disabled={savingBid}>
                              {t('campaigns.googleBidding.cancel', 'Cancelar')}
                            </Button>
                          </div>
                          </>)}
                        </div>
                      )}

                      {/* Targets */}
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {googleSettings.targetCpa != null && (
                          <div className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">{t('campaigns.googleSettings.targetCpa', 'CPA alvo')}: </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              R$ {googleSettings.targetCpa.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                        {googleSettings.targetSpend != null && (
                          <div className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">{t('campaigns.googleSettings.targetSpend', 'Investimento alvo')}: </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              R$ {googleSettings.targetSpend.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        )}
                        {googleSettings.targetRoas != null && (
                          <div className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">{t('campaigns.googleSettings.targetRoas', 'ROAS alvo')}: </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {(googleSettings.targetRoas * 100).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}%
                            </span>
                          </div>
                        )}
                        {googleSettings.optimizationScore != null && (
                          <div className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">{t('campaigns.googleSettings.optScore', 'Pontuação de otimização')}: </span>
                            <span className="font-medium text-gray-900 dark:text-white">
                              {Math.round(googleSettings.optimizationScore * 100)}%
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Desempenho da estratégia de lance (RMF R.130) */}
                      <div className="mt-4">
                        <GoogleBiddingReport
                          portfolioResource={currentPortfolioRn}
                          portfolioName={currentPortfolio?.name || null}
                          biddingStrategyType={googleSettings.biddingStrategyType}
                        />
                      </div>
                    </div>

                    {/* Metas de conversão (agrupadas por categoria) */}
                    <div>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {t('campaigns.googleGoals.title', 'Metas de conversão')}
                        </p>
                        {!editingGoals && (
                          <div className="flex items-center flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="secondary"
                              leftIcon={<Plus className="w-4 h-4" />}
                              onClick={openNewGoalModal}
                            >
                              {t('campaigns.googleNewGoal.button', 'Criar nova meta')}
                            </Button>
                            {googleSettings.conversionGoals.length > 0 && (
                              <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<Pencil className="w-4 h-4" />}
                                onClick={startEditingGoals}
                              >
                                {t('campaigns.googleGoals.edit', 'Editar metas')}
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      {googleSettings.conversionGoals.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {t('campaigns.googleSettings.noGoals', 'Nenhuma meta de conversão configurada no Google.')}
                        </p>
                      ) : editingGoals ? (
                        <div className="space-y-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('campaigns.googleGoals.hint', 'Escolha quais categorias de conversão o Google usa para otimizar os lances.')}
                          </p>
                          <div className="space-y-2">
                            {Object.entries(getGoalCategoryGroups()).map(([cat, group]) => (
                              <label
                                key={cat}
                                className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                  checked={!!goalCats[cat]}
                                  onChange={(e) => setGoalCats((prev) => ({ ...prev, [cat]: e.target.checked }))}
                                  disabled={savingGoals}
                                />
                                <div className="min-w-0">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">{group.categoryLabel}</span>
                                  <div className="mt-0.5 flex flex-col gap-0.5">
                                    {group.names.map((name, ni) => (
                                      <span key={`${name}-${ni}`} className="text-xs text-gray-500 dark:text-gray-400">
                                        {name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={handleSaveGoals} isLoading={savingGoals} disabled={savingGoals}>
                              {t('campaigns.googleGoals.save', 'Salvar')}
                            </Button>
                            <Button size="sm" variant="secondary" onClick={() => setEditingGoals(false)} disabled={savingGoals}>
                              {t('campaigns.googleGoals.cancel', 'Cancelar')}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {Object.entries(getGoalCategoryGroups()).map(([cat, group]) => (
                            <div
                              key={cat}
                              className="px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                            >
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{group.categoryLabel}</span>
                                {group.active && (
                                  <Badge variant="primary">
                                    <span className="flex items-center gap-1">
                                      <Sparkles className="w-3 h-3" />
                                      {t('campaigns.googleGoals.counts', 'conta para o lance')}
                                    </span>
                                  </Badge>
                                )}
                              </div>
                              <div className="mt-1 flex flex-col items-start gap-0.5">
                                {group.goals.map((goal, ni) => (
                                  <button
                                    key={`${goal.id}-${ni}`}
                                    type="button"
                                    onClick={() => openSnippetModal(goal.id, goal.name)}
                                    title={t('campaigns.googleSnippet.view', 'Ver snippet')}
                                    className="group/snippet inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                  >
                                    <span className="underline decoration-dotted underline-offset-2">{goal.name}</span>
                                    <Code className="w-3 h-3 opacity-60 group-hover/snippet:opacity-100" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Footer note */}
                    <p className="text-xs text-gray-400 dark:text-gray-500">
                      {t('campaigns.googleSettings.editNote', 'Para alterar objetivo, orçamento ou lance, use o botão Editar. As metas de conversão são gerenciadas no Google Ads.')}
                    </p>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          {/* Platform Status */}
          <Card padding={false} className="p-4 sm:p-6">
            <div className="mb-4">
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                {t('campaigns.platformStatus.title')}
              </CardTitle>
            </div>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {/* ChatGPT Ads — status simples (gestão fina fica no Ads Manager da OpenAI) */}
                {(campaign.platform === 'openai' || campaign.platforms?.includes('openai')) && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">ChatGPT Ads</span>
                      {campaign.external_id_openai ? (
                        <Badge variant="success">{t('campaigns.platformStatus.published', 'Publicada')}</Badge>
                      ) : (
                        <Badge variant="gray">{t('campaigns.platformStatus.notPublished', 'Não publicada')}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('campaigns.platformStatus.openaiManaged', 'Sincronizada do Ads Manager da OpenAI — pausar/ativar funciona por aqui; edição de criativos, por lá.')}
                    </p>
                  </div>
                )}
                {/* Google Ads */}
                {(campaign.platform === 'google' || campaign.platform === 'both' || campaign.platform === 'all' || campaign.platforms?.includes('google')) && (() => {
                  const result = inlinePlatformResults?.google;
                  const hasError = result && !result.skipped && !result.success;
                  const hasWarning = result && result.success && result.warning;
                  const externalId = campaign.external_id_google;
                  const isPushing = pushingPlatform === 'google';
                  return (
                    <div className={`p-4 rounded-lg border ${hasError ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20' : externalId ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                          </div>
                          <span className="font-semibold text-gray-900 dark:text-white">Google Ads</span>
                        </div>
                        {hasError ? <XCircle className="w-5 h-5 text-red-500" /> : externalId ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <MinusCircle className="w-5 h-5 text-gray-400" />}
                      </div>
                      {externalId ? (
                        <div className="space-y-1">
                          <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-1"><Check className="w-3.5 h-3.5" />{t('campaigns.platformStatus.campaignCreated')}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 break-all">ID: {externalId}</p>
                          {/* Live platform status pulled directly from Google Ads API */}
                          {loadingPlatformStatus && !platformStatus && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              {t('campaigns.platformStatus.loading', 'Buscando status ao vivo...')}
                            </p>
                          )}
                          {platformStatus?.google?.campaign && (
                            <div className="mt-1.5 pt-1.5 border-t border-green-200 dark:border-green-800">
                              <p className="text-xs">
                                <span className="text-gray-500 dark:text-gray-400">{t('campaigns.platformStatus.liveStatus', 'Status ao vivo')}:</span>{' '}
                                <span className={`font-semibold ${
                                  platformStatus.google.campaign.status === 'ENABLED' ? 'text-green-700 dark:text-green-300' :
                                  platformStatus.google.campaign.status === 'PAUSED' ? 'text-yellow-700 dark:text-yellow-300' :
                                  'text-gray-700 dark:text-gray-300'
                                }`} title={platformStatus.google.campaign.status}>
                                  {String(t(`ads.adStrength.googleCampaignValues.${platformStatus.google.campaign.status}`, platformStatus.google.campaign.status))}
                                </span>
                              </p>
                              {platformStatus.google.campaign.servingStatus && platformStatus.google.campaign.servingStatus !== platformStatus.google.campaign.status && (
                                <p className="text-xs text-gray-500 dark:text-gray-400" title={platformStatus.google.campaign.servingStatus}>
                                  {t('campaigns.platformStatus.serving', 'Veiculando')}: {platformStatus.google.campaign.servingStatus}
                                </p>
                              )}
                              <button
                                onClick={() => loadPlatformStatus()}
                                disabled={loadingPlatformStatus}
                                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 mt-0.5 disabled:opacity-50"
                              >
                                <RefreshCw className={`w-3 h-3 ${loadingPlatformStatus ? 'animate-spin' : ''}`} />
                                {t('campaigns.platformStatus.refresh', 'Atualizar')}
                              </button>
                            </div>
                          )}
                          {platformStatus?.google?.error && (
                            <p className="text-xs text-red-500 mt-1 break-words">⚠ {platformStatus.google.error === 'not_connected' ? t('campaigns.platformStatus.notConnected', 'Não conectado') : platformStatus.google.error}</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{campaign.status === 'active' ? t('campaigns.platformStatus.pendingSync') : t('campaigns.platformStatus.notSynced')}</p>
                      )}
                      {/* Last result detail */}
                      {result && !result.skipped && (
                        <div className={`mt-2 pt-2 border-t ${result.success ? 'border-green-200 dark:border-green-700' : 'border-red-200 dark:border-red-700'}`}>
                          <div className={`text-xs flex items-start gap-1.5 ${result.success ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-400'}`}>
                            {result.success ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                            <span className="break-words">
                              {result.success
                                ? (result.action === 'created' ? t('campaigns.toggleResult.created') : t('campaigns.toggleResult.success'))
                                : (result.error || t('campaigns.toggleResult.error'))}
                            </span>
                          </div>
                          {hasWarning && (
                            <div className="text-xs flex items-start gap-1.5 text-yellow-600 dark:text-yellow-400 mt-1">
                              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span className="break-words">{result.userMessage || result.warning}</span>
                            </div>
                          )}
                          {hasError && (
                            <button
                              onClick={() => handlePushToPlatform('google')}
                              disabled={isPushing}
                              className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 disabled:opacity-50"
                            >
                              {isPushing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                              {isPushing ? t('campaigns.toggleResult.retrying') : t('campaigns.toggleResult.retry')}
                            </button>
                          )}
                        </div>
                      )}
                      {/* Push button when not synced */}
                      {!externalId && !result && (
                        <button
                          onClick={() => handlePushToPlatform('google')}
                          disabled={isPushing}
                          className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 disabled:opacity-50"
                        >
                          {isPushing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                          {isPushing ? t('campaigns.toggleResult.pushing') : t('campaigns.toggleResult.pushToPlatform')}
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* Meta Ads */}
                {(campaign.platform === 'meta' || campaign.platform === 'both' || campaign.platform === 'all' || campaign.platforms?.includes('meta')) && (() => {
                  const result = inlinePlatformResults?.meta;
                  const metaSyncStatus = (campaign as any).settings?.meta_sync_status;
                  const metaSyncFailed = metaSyncStatus === 'failed';
                  const metaSyncPartial = metaSyncStatus === 'partial';
                  const hasError = (result && !result.skipped && !result.success) || metaSyncFailed;
                  const hasWarning = (result && result.success && result.warning) || metaSyncPartial;
                  const externalId = campaign.external_id_meta;
                  const isSynced = externalId && !metaSyncFailed;
                  const isPushing = pushingPlatform === 'meta';
                  return (
                    <div className={`p-4 rounded-lg border ${hasError ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20' : isSynced ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm">
                            <img src="/logo_meta.png" alt="Meta" className="w-5 h-5" />
                          </div>
                          <span className="font-semibold text-gray-900 dark:text-white">Meta Ads</span>
                        </div>
                        {hasError ? <XCircle className="w-5 h-5 text-red-500" /> : isSynced ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <MinusCircle className="w-5 h-5 text-gray-400" />}
                      </div>
                      {isSynced ? (
                        <div className="space-y-1">
                          <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-1"><Check className="w-3.5 h-3.5" />{t('campaigns.platformStatus.campaignCreated')}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 break-all">ID: {externalId}</p>
                          {/* Live platform status pulled directly from Meta Graph API */}
                          {loadingPlatformStatus && !platformStatus && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              {t('campaigns.platformStatus.loading', 'Buscando status ao vivo...')}
                            </p>
                          )}
                          {platformStatus?.meta?.campaign && (
                            <div className="mt-1.5 pt-1.5 border-t border-green-200 dark:border-green-800">
                              <p className="text-xs">
                                <span className="text-gray-500 dark:text-gray-400">{t('campaigns.platformStatus.liveStatus', 'Status ao vivo')}:</span>{' '}
                                <span className={`font-semibold ${
                                  platformStatus.meta.campaign.effectiveStatus === 'ACTIVE' ? 'text-green-700 dark:text-green-300' :
                                  platformStatus.meta.campaign.effectiveStatus === 'PAUSED' ? 'text-yellow-700 dark:text-yellow-300' :
                                  platformStatus.meta.campaign.effectiveStatus === 'DISAPPROVED' ? 'text-red-700 dark:text-red-300' :
                                  'text-gray-700 dark:text-gray-300'
                                }`} title={platformStatus.meta.campaign.effectiveStatus}>
                                  {String(t(`ads.adStrength.metaEffectiveValues.${platformStatus.meta.campaign.effectiveStatus}`, platformStatus.meta.campaign.effectiveStatus))}
                                </span>
                              </p>
                              {platformStatus.meta.campaign.issues && platformStatus.meta.campaign.issues.length > 0 && (
                                <p className="text-xs text-orange-600 dark:text-orange-400 flex items-center gap-1 mt-0.5">
                                  <AlertTriangle className="w-3 h-3" />
                                  {platformStatus.meta.campaign.issues.length} {t('campaigns.platformStatus.issues', 'problema(s) reportado(s)')}
                                </p>
                              )}
                              <button
                                onClick={() => loadPlatformStatus()}
                                disabled={loadingPlatformStatus}
                                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 mt-0.5 disabled:opacity-50"
                              >
                                <RefreshCw className={`w-3 h-3 ${loadingPlatformStatus ? 'animate-spin' : ''}`} />
                                {t('campaigns.platformStatus.refresh', 'Atualizar')}
                              </button>
                            </div>
                          )}
                          {platformStatus?.meta?.error && (
                            <p className="text-xs text-red-500 mt-1 break-words">⚠ {platformStatus.meta.error === 'not_connected' ? t('campaigns.platformStatus.notConnected', 'Não conectado') : platformStatus.meta.error}</p>
                          )}
                        </div>
                      ) : metaSyncFailed ? (
                        <div className="space-y-1">
                          <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />Campanha incompleta - conjunto de anúncios não foi criado</p>
                          {(campaign as any).settings?.meta_sync_errors?.map((err: string, i: number) => (
                            <p key={i} className="text-xs text-red-500 dark:text-red-400">{err}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{campaign.status === 'active' ? t('campaigns.platformStatus.pendingSync') : t('campaigns.platformStatus.notSynced')}</p>
                      )}
                      {result && !result.skipped && (
                        <div className={`mt-2 pt-2 border-t ${result.success ? 'border-green-200 dark:border-green-700' : 'border-red-200 dark:border-red-700'}`}>
                          <div className={`text-xs flex items-start gap-1.5 ${result.success ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-400'}`}>
                            {result.success ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                            <span className="break-words">
                              {result.success
                                ? (result.action === 'created' ? t('campaigns.toggleResult.created') : t('campaigns.toggleResult.success'))
                                : (result.error || t('campaigns.toggleResult.error'))}
                            </span>
                          </div>
                          {hasWarning && (
                            <div className="text-xs flex items-start gap-1.5 text-yellow-600 dark:text-yellow-400 mt-1">
                              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span className="break-words">{result.userMessage || result.warning}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {(hasError || metaSyncFailed || (!externalId && !result)) && (
                        <button
                          onClick={() => handlePushToPlatform('meta')}
                          disabled={isPushing}
                          className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 disabled:opacity-50"
                        >
                          {isPushing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (hasError || metaSyncFailed) ? <RefreshCw className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
                          {isPushing ? t('campaigns.toggleResult.pushing') : (hasError || metaSyncFailed) ? t('campaigns.toggleResult.retry') : t('campaigns.toggleResult.pushToPlatform')}
                        </button>
                      )}
                    </div>
                  );
                })()}

                {/* LinkedIn Ads */}
                {(campaign.platform === 'linkedin' || campaign.platform === 'all' || campaign.platforms?.includes('linkedin')) && (() => {
                  const result = inlinePlatformResults?.linkedin;
                  const linkedinSyncStatus = (campaign as any).settings?.linkedin_sync_status;
                  const linkedinSyncFailed = linkedinSyncStatus === 'failed';
                  const linkedinSyncPartial = linkedinSyncStatus === 'partial';
                  const hasError = (result && !result.skipped && !result.success) || linkedinSyncFailed;
                  const hasWarning = (result && result.success && result.warning) || linkedinSyncPartial;
                  const externalId = campaign.external_id_linkedin;
                  const isSynced = externalId && !linkedinSyncFailed;
                  const isPushing = pushingPlatform === 'linkedin';
                  return (
                    <div className={`p-4 rounded-lg border ${hasError ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20' : isSynced ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center shadow-sm">
                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/>
                            </svg>
                          </div>
                          <span className="font-semibold text-gray-900 dark:text-white">LinkedIn Ads</span>
                        </div>
                        {hasError ? <XCircle className="w-5 h-5 text-red-500" /> : isSynced ? <CheckCircle2 className="w-5 h-5 text-green-500" /> : <MinusCircle className="w-5 h-5 text-gray-400" />}
                      </div>
                      {isSynced ? (
                        <div className="space-y-1">
                          <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-1"><Check className="w-3.5 h-3.5" />{t('campaigns.platformStatus.campaignCreated')}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 break-all">ID: {externalId}</p>
                          {/* Live platform status pulled directly from LinkedIn Ads API */}
                          {loadingPlatformStatus && !platformStatus && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                              <Loader2 className="w-3 h-3 animate-spin" />
                              {t('campaigns.platformStatus.loading', 'Buscando status ao vivo...')}
                            </p>
                          )}
                          {platformStatus?.linkedin?.campaign && (
                            <div className="mt-1.5 pt-1.5 border-t border-green-200 dark:border-green-800">
                              <p className="text-xs">
                                <span className="text-gray-500 dark:text-gray-400">{t('campaigns.platformStatus.liveStatus', 'Status ao vivo')}:</span>{' '}
                                <span className={`font-semibold ${
                                  platformStatus.linkedin.campaign.status === 'ACTIVE' ? 'text-green-700 dark:text-green-300' :
                                  platformStatus.linkedin.campaign.status === 'PAUSED' ? 'text-yellow-700 dark:text-yellow-300' :
                                  'text-gray-700 dark:text-gray-300'
                                }`} title={platformStatus.linkedin.campaign.status}>
                                  {String(t(`ads.adStrength.linkedinValues.${platformStatus.linkedin.campaign.status}`, platformStatus.linkedin.campaign.status))}
                                </span>
                              </p>
                              {platformStatus.linkedin.campaign.servingStatuses && platformStatus.linkedin.campaign.servingStatuses.length > 0 && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {t('campaigns.platformStatus.serving', 'Serving')}: {platformStatus.linkedin.campaign.servingStatuses.join(', ')}
                                </p>
                              )}
                              <button
                                onClick={() => loadPlatformStatus()}
                                disabled={loadingPlatformStatus}
                                className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 mt-0.5 disabled:opacity-50"
                              >
                                <RefreshCw className={`w-3 h-3 ${loadingPlatformStatus ? 'animate-spin' : ''}`} />
                                {t('campaigns.platformStatus.refresh', 'Atualizar')}
                              </button>
                            </div>
                          )}
                          {platformStatus?.linkedin?.error && (
                            <p className="text-xs text-red-500 mt-1 break-words">⚠ {platformStatus.linkedin.error === 'not_connected' ? t('campaigns.platformStatus.notConnected', 'Não conectado') : platformStatus.linkedin.error}</p>
                          )}
                        </div>
                      ) : linkedinSyncFailed ? (
                        <div className="space-y-1">
                          <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />Campanha criada, mas os anúncios falharam</p>
                          {(campaign as any).settings?.linkedin_sync_errors?.map((err: string, i: number) => (
                            <p key={i} className="text-xs text-red-500 dark:text-red-400">{err}</p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">{campaign.status === 'active' ? t('campaigns.platformStatus.pendingSync') : t('campaigns.platformStatus.notSynced')}</p>
                      )}
                      {result && !result.skipped && (
                        <div className={`mt-2 pt-2 border-t ${result.success ? 'border-green-200 dark:border-green-700' : 'border-red-200 dark:border-red-700'}`}>
                          <div className={`text-xs flex items-start gap-1.5 ${result.success ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-400'}`}>
                            {result.success ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                            <span className="break-words">
                              {result.success
                                ? (result.action === 'created' ? t('campaigns.toggleResult.created') : t('campaigns.toggleResult.success'))
                                : (result.error || t('campaigns.toggleResult.error'))}
                            </span>
                          </div>
                          {hasWarning && (
                            <div className="text-xs flex items-start gap-1.5 text-yellow-600 dark:text-yellow-400 mt-1">
                              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span className="break-words">{result.userMessage || result.warning}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {(hasError || linkedinSyncFailed || (!externalId && !result)) && (
                        <button
                          onClick={() => handlePushToPlatform('linkedin')}
                          disabled={isPushing}
                          className="mt-2 flex items-center gap-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 disabled:opacity-50"
                        >
                          {isPushing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (hasError || linkedinSyncFailed) ? <RefreshCw className="w-3.5 h-3.5" /> : <ExternalLink className="w-3.5 h-3.5" />}
                          {isPushing ? t('campaigns.toggleResult.pushing') : (hasError || linkedinSyncFailed) ? t('campaigns.toggleResult.retry') : t('campaigns.toggleResult.pushToPlatform')}
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>

              {campaign.last_synced_at && (
                <p className="text-xs text-gray-400 mt-3">
                  {t('campaigns.platformStatus.lastSync')}: {new Date(campaign.last_synced_at).toLocaleString('pt-BR')}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Chart */}
          <Card padding={false} className="p-4 sm:p-6">
            <div className="mb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle>{t('dashboard.performance')}</CardTitle>
                {/* Filtro de período: recarrega os métricos do chart com o intervalo escolhido */}
                <div className="flex items-center gap-2 text-sm w-full sm:w-auto min-w-0">
                  <input
                    type="date"
                    value={chartStartDate}
                    max={chartEndDate}
                    onChange={async (e) => {
                      const newStart = e.target.value;
                      setChartStartDate(newStart);
                      setChartLoading(true);
                      try {
                        const res = await campaignService.getCampaignMetrics(id!, newStart, chartEndDate);
                        if (res.success) setMetrics(res.data);
                      } finally { setChartLoading(false); }
                    }}
                    className="flex-1 sm:flex-none min-w-0 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                  />
                  <span className="text-gray-400 flex-shrink-0">—</span>
                  <input
                    type="date"
                    value={chartEndDate}
                    min={chartStartDate}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={async (e) => {
                      const newEnd = e.target.value;
                      setChartEndDate(newEnd);
                      setChartLoading(true);
                      try {
                        const res = await campaignService.getCampaignMetrics(id!, chartStartDate, newEnd);
                        if (res.success) setMetrics(res.data);
                      } finally { setChartLoading(false); }
                    }}
                    className="flex-1 sm:flex-none min-w-0 px-2 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
            <CardContent>
              <div className={`h-60 sm:h-80 min-w-0 ${chartLoading ? 'opacity-50' : ''}`}>
                {metrics?.daily && metrics.daily.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={metrics.daily}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                      <XAxis dataKey="date" stroke="#9ca3af" fontSize={11} minTickGap={16} />
                      <YAxis stroke="#9ca3af" fontSize={11} width={40} />
                      <Tooltip contentStyle={{ backgroundColor: 'rgba(17, 24, 39, 0.9)', border: 'none', borderRadius: '8px', color: '#fff' }} />
                      <Line type="monotone" dataKey="clicks" stroke="#5b7bf1" strokeWidth={2} name={t('campaigns.metrics.clicks')} dot={false} />
                      <Line type="monotone" dataKey="impressions" stroke="#d946ef" strokeWidth={2} name={t('campaigns.metrics.impressions')} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">{t('common.noData')}</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Campaign Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
            <Card padding={false} className="p-4 sm:p-6">
              <div className="mb-4"><CardTitle>{t('common.description')}</CardTitle></div>
              <CardContent>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.platform')}</dt>
                    <dd className="font-medium text-gray-900 dark:text-white capitalize">{campaign.platform === 'openai' ? 'ChatGPT Ads' : campaign.platform}</dd>
                  </div>
                  {/* Platform Hierarchy */}
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.hierarchy.title', 'Estrutura')}</dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                      {(campaign.platform === 'meta' || campaign.platforms?.includes('meta')) && (
                        <div className="flex items-center gap-1 text-sm flex-wrap">
                          <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded text-blue-700 dark:text-blue-300">Campanha</span>
                          <ChevronRight className="w-3 h-3 text-gray-400" />
                          <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded text-blue-700 dark:text-blue-300">
                            {campaign.settings?.ad_set_name || t('campaigns.hierarchy.adSet', 'Conjunto de Anuncios')}
                          </span>
                          <ChevronRight className="w-3 h-3 text-gray-400" />
                          <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 rounded text-blue-700 dark:text-blue-300">Anuncios</span>
                        </div>
                      )}
                      {(campaign.platform === 'linkedin' || campaign.platforms?.includes('linkedin')) && (
                        <div className="flex items-center gap-1 text-sm flex-wrap">
                          <span className="px-1.5 py-0.5 bg-sky-100 dark:bg-sky-900/30 rounded text-sky-700 dark:text-sky-300">
                            {campaign.settings?.campaign_group_name || t('campaigns.hierarchy.campaignGroup', 'Grupo de Campanhas')}
                          </span>
                          <ChevronRight className="w-3 h-3 text-gray-400" />
                          <span className="px-1.5 py-0.5 bg-sky-100 dark:bg-sky-900/30 rounded text-sky-700 dark:text-sky-300">Campanha</span>
                          <ChevronRight className="w-3 h-3 text-gray-400" />
                          <span className="px-1.5 py-0.5 bg-sky-100 dark:bg-sky-900/30 rounded text-sky-700 dark:text-sky-300">Anuncios</span>
                        </div>
                      )}
                      {(campaign.platform === 'google' || campaign.platform === 'both' || campaign.platforms?.includes('google')) && (
                        <div className="flex items-center gap-1 text-sm flex-wrap">
                          <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 rounded text-green-700 dark:text-green-300">Campanha</span>
                          <ChevronRight className="w-3 h-3 text-gray-400" />
                          <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 rounded text-green-700 dark:text-green-300">Anuncios</span>
                        </div>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.objective')}</dt>
                    <dd className="font-medium text-gray-900 dark:text-white">{objetivoLabel(campaign.objective)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.budget')}</dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(campaign.budget_amount || 0)} / {campaign.budget_type === 'daily' ? t('campaigns.dailyBudget') : t('campaigns.lifetimeBudget')}
                    </dd>
                  </div>
                  {campaign.description && (
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-gray-400">{t('common.description')}</dt>
                      <dd className="font-medium text-gray-900 dark:text-white break-words">{campaign.description}</dd>
                    </div>
                  )}
                </dl>
              </CardContent>
            </Card>
            <Card padding={false} className="p-4 sm:p-6">
              <div className="mb-4"><CardTitle>{t('campaigns.targeting')}</CardTitle></div>
              <CardContent>
                <dl className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <dt className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.startDate')}:</dt>
                    <dd className="font-medium text-gray-900 dark:text-white">{campaign.start_date ? new Date(campaign.start_date).toLocaleDateString('pt-BR') : '-'}</dd>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <dt className="text-sm text-gray-500 dark:text-gray-400">{t('campaigns.endDate')}:</dt>
                    <dd className="font-medium text-gray-900 dark:text-white">{campaign.end_date ? new Date(campaign.end_date).toLocaleDateString('pt-BR') : '-'}</dd>
                  </div>
                  {campaign.target_audience && (
                    <>
                      <div>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Idade</dt>
                        <dd className="font-medium text-gray-900 dark:text-white">{campaign.target_audience.age_min} - {campaign.target_audience.age_max} anos</dd>
                      </div>
                      {campaign.target_audience.locations?.length > 0 && (
                        <div>
                          <dt className="text-sm text-gray-500 dark:text-gray-400">Localizações</dt>
                          <dd className="font-medium text-gray-900 dark:text-white break-words">{campaign.target_audience.locations.map((loc: any) => typeof loc === 'string' ? loc : loc.name || loc.canonicalName || String(loc)).join(', ')}</dd>
                        </div>
                      )}
                    </>
                  )}
                </dl>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {activeTab === 'ads' && (() => {
        const counts = {
          all: ads.length,
          active: ads.filter(a => a.status === 'active').length,
          paused: ads.filter(a => a.status === 'paused').length,
          inactive: ads.filter(a => a.status !== 'active' && a.status !== 'paused').length
        };
        const filteredAds = adsStatusFilter === 'all'
          ? ads
          : adsStatusFilter === 'inactive'
          ? ads.filter(a => a.status !== 'active' && a.status !== 'paused')
          : ads.filter(a => a.status === adsStatusFilter);
        return (
        <Card padding={false} className="p-4 sm:p-6">
          <div className="mb-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <CardTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5" />
                {t('ads.title')}
                {ads.length > 0 && (
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({adsStatusFilter === 'all' ? ads.length : `${filteredAds.length}/${ads.length}`})
                  </span>
                )}
              </CardTitle>
              <Link
                to={`/campaigns/${id}/edit?step=creatives`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors whitespace-nowrap"
              >
                <Plus className="w-4 h-4" />
                {t('ads.createAd', 'Novo Anúncio')}
              </Link>
            </div>
            {ads.length > 0 && (
              <div className="grid grid-cols-4 sm:inline-flex items-center gap-1 mt-3 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full sm:w-fit">
                {([
                  { key: 'all', label: t('ads.filter.all', 'Todos'), count: counts.all },
                  { key: 'active', label: t('ads.filter.active', 'Ativos'), count: counts.active },
                  { key: 'paused', label: t('ads.filter.paused', 'Pausados'), count: counts.paused },
                  { key: 'inactive', label: t('ads.filter.inactive', 'Outros'), count: counts.inactive },
                ] as const).map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => setAdsStatusFilter(opt.key as typeof adsStatusFilter)}
                    className={`px-1.5 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap min-w-0 truncate ${
                      adsStatusFilter === opt.key
                        ? 'bg-white dark:bg-gray-700 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {opt.label} ({opt.count})
                  </button>
                ))}
              </div>
            )}
          </div>
          <CardContent>
            {loadingAds ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : ads.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400 mb-4">{t('ads.noAds')}</p>
                <Link
                  to={`/campaigns/${id}/edit?step=creatives`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {t('ads.createFirstAd', 'Criar primeiro anúncio')}
                </Link>
              </div>
            ) : filteredAds.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400">
                  {t('ads.filter.empty', 'Nenhum anúncio com esse filtro.')}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAds.map((ad) => {
                  const campaignIsActive = campaign?.status === 'active' || campaign?.status === 'paused';
                  const isDraftAdInDraftCampaign = ad.status === 'draft' && !campaignIsActive;
                  const canToggle = (ad.status === 'active' || ad.status === 'paused' || ad.status === 'draft') && !isDraftAdInDraftCampaign;
                  const isActivating = ad.status !== 'active';
                  return (
                    <div
                      key={ad.id}
                      className={`p-3 sm:p-4 rounded-lg border transition-colors min-w-0 ${
                        ad.status === 'active'
                          ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                          : ad.status === 'paused'
                          ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'
                          : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap min-w-0">
                            <h4 className="font-medium text-gray-900 dark:text-white break-words min-w-0">{ad.name}</h4>
                            <Badge variant={
                              ad.status === 'active' ? 'success' :
                              ad.status === 'paused' ? 'warning' :
                              ad.status === 'rejected' ? 'danger' :
                              'gray'
                            }>
                              {ad.status === 'active' ? t('ads.status.active') :
                               ad.status === 'paused' ? t('ads.status.paused') :
                               ad.status === 'draft' ? t('ads.status.draft') :
                               ad.status === 'pending_review' ? t('ads.status.pendingReview') :
                               ad.status === 'rejected' ? t('ads.status.rejected') :
                               ad.status}
                            </Badge>
                            {ad.status === 'draft' && (ad.external_id_google || ad.external_id_meta || ad.external_id_linkedin) && (
                              <span className="text-xs text-orange-600 dark:text-orange-400" title={t('ads.draftButSyncedTooltip', 'Criado na plataforma mas ainda marcado como rascunho localmente')}>
                                ({t('ads.draftButSynced', 'sincronizado')})
                              </span>
                            )}
                            {isDraftAdInDraftCampaign && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 italic" title={t('ads.publishCampaignFirstTooltip', 'Publique a campanha para ativar os anúncios')}>
                                — {t('ads.awaitingCampaign', 'aguardando publicação da campanha')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400 flex-wrap min-w-0">
                            <span className="capitalize">{ad.type}</span>
                            {ad.headline && <span className="truncate max-w-full sm:max-w-xs">{ad.headline}</span>}
                            {ad.external_id_google && <span className="hidden sm:inline break-all">Google: {ad.external_id_google}</span>}
                            {ad.external_id_meta && <span className="hidden sm:inline break-all">Meta: {ad.external_id_meta}</span>}
                            {ad.external_id_linkedin && <span className="hidden sm:inline break-all">LinkedIn: {ad.external_id_linkedin}</span>}
                            {(() => {
                              const adMetaPageId = (ad.settings as any)?.meta_page_id;
                              if (adMetaPageId) {
                                const page = metaPages.find(p => String(p.page_id) === String(adMetaPageId));
                                if (page) {
                                  return (
                                    <span className="inline-flex items-center gap-1">
                                      {page.page_picture ? (
                                        <img src={page.page_picture} alt="" className="w-4 h-4 rounded-full object-cover" />
                                      ) : null}
                                      <span className="truncate max-w-[140px]">{page.page_name}</span>
                                    </span>
                                  );
                                }
                              }
                              const adLiOrgUrn = (ad.settings as any)?.linkedin_org_urn;
                              if (adLiOrgUrn) {
                                const org = linkedinOrgs.find(o => o.organization_urn === adLiOrgUrn);
                                if (org) {
                                  return (
                                    <span className="inline-flex items-center gap-1">
                                      {org.logo_url ? (
                                        <img src={org.logo_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                                      ) : null}
                                      <span className="truncate max-w-[140px]">{org.name}</span>
                                    </span>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </div>
                          {/* Live approval + quality badges pulled directly from the platforms */}
                          {(() => {
                            // Match local ad to live platform data
                            const googleLive = platformStatus?.google?.ads?.find((pa: any) => {
                              const adIdRight = ad.external_id_google?.split('~').pop();
                              return pa.external_id === ad.external_id_google || pa.external_id === adIdRight;
                            });
                            const metaLive = platformStatus?.meta?.ads?.find((pa: any) => pa.external_id === ad.external_id_meta);
                            const linkedinLive = platformStatus?.linkedin?.ads?.find((pa: any) => pa.external_id === ad.external_id_linkedin);

                            if (!googleLive && !metaLive && !linkedinLive) return null;

                            const approvalColor = (s: string | undefined) => {
                              if (!s) return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                              const up = s.toUpperCase();
                              if (up.includes('APPROVED') && !up.includes('DISAPPROVED')) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
                              if (up === 'DISAPPROVED' || up.includes('REJECTED')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
                              if (up.includes('REVIEW') || up.includes('PENDING')) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
                              return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                            };
                            const strengthColor = (s: string | undefined) => {
                              if (!s) return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                              const up = s.toUpperCase();
                              if (up === 'EXCELLENT' || up === 'GOOD') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
                              if (up === 'AVERAGE') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
                              if (up === 'POOR' || up === 'NO_ADS') return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
                              return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                            };
                            const rankingColor = (r: string | undefined) => {
                              if (!r) return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                              const up = r.toUpperCase();
                              if (up.includes('ABOVE_AVERAGE')) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
                              if (up === 'AVERAGE') return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300';
                              if (up.includes('BELOW_AVERAGE')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
                              return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
                            };

                            // Build actionable suggestions — each check is DATA-DRIVEN (no hardcoded filler).
                            // Only show a suggestion when we can verify from the ad/campaign data that it applies.
                            const suggestions: string[] = [];
                            const strength = (googleLive?.adStrength || '').toUpperCase();
                            const qRanking = (metaLive?.qualityRanking || '').toUpperCase();
                            const engRanking = (metaLive?.engagementRateRanking || '').toUpperCase();
                            const needsGoogleHelp = strength === 'POOR' || strength === 'AVERAGE';
                            const needsMetaHelp = qRanking.includes('BELOW_AVERAGE') || engRanking.includes('BELOW_AVERAGE');

                            if (needsGoogleHelp) {
                              const validHeadlines = (ad.headlines || []).filter(h => h && h.trim());
                              const validDescs = (ad.descriptions || []).filter(d => d && d.trim());

                              // ① Quantity — only if below Google's ideal
                              if (validHeadlines.length < 10) suggestions.push(String(t('ads.adStrength.suggestions.addHeadlines', 'Adicione mais títulos (ideal: 10-15)')));
                              if (validDescs.length < 3) suggestions.push(String(t('ads.adStrength.suggestions.addDescriptions', 'Adicione mais descrições (ideal: 3-4)')));

                              // ② Uniqueness — only if there are actual duplicates
                              if (validHeadlines.length > 0) {
                                const uniqueHeadlines = new Set(validHeadlines.map(h => h.toLowerCase().trim()));
                                if (uniqueHeadlines.size < validHeadlines.length) {
                                  suggestions.push(String(t('ads.adStrength.suggestions.uniqueHeadlines', 'Use títulos mais únicos (evite repetição)')));
                                }
                              }

                              // ③ Length variation — only if ≥3 headlines AND spread < 10 chars
                              if (validHeadlines.length >= 3) {
                                const lens = validHeadlines.map(h => h.length);
                                const minLen = Math.min(...lens);
                                const maxLen = Math.max(...lens);
                                if (maxLen - minLen < 10) {
                                  suggestions.push(String(t('ads.adStrength.suggestions.variedLengths', 'Varie o tamanho dos títulos (misture curtos e longos)')));
                                }
                              }

                              // ④ Extensions — only suggest those actually missing
                              const googleAssets = (campaign as any)?.settings?.google_assets || {};
                              const hasCallouts = Array.isArray(googleAssets.callouts) && googleAssets.callouts.length > 0;
                              const hasSnippets = Array.isArray(googleAssets.structuredSnippets) && googleAssets.structuredSnippets.length > 0;
                              const hasSitelinks = Array.isArray(googleAssets.sitelinks) && googleAssets.sitelinks.length > 0;
                              const hasWhatsapp = googleAssets.whatsapp?.enabled === true && !!googleAssets.whatsapp?.phone_number;
                              const missingExt: string[] = [];
                              if (!hasCallouts) missingExt.push('Callouts');
                              if (!hasSnippets) missingExt.push(String(t('campaigns.new.structuredSnippets', 'Snippets estruturados')));
                              if (!hasSitelinks) missingExt.push('Sitelinks');
                              if (!hasWhatsapp) missingExt.push('WhatsApp');
                              if (missingExt.length > 0) {
                                suggestions.push(String(t('ads.adStrength.suggestions.addMissingExtensions', 'Adicione extensões que faltam: {{list}}')).replace('{{list}}', missingExt.join(', ')));
                              }

                              // ⑤ Keywords — only if this ad has zero keyword references saved
                              const adKeywords = (ad.settings as any)?.keywords;
                              const hasKeywords = Array.isArray(adKeywords) && adKeywords.length > 0;
                              if (!hasKeywords && validHeadlines.length > 0) {
                                suggestions.push(String(t('ads.adStrength.suggestions.includeKeywords', 'Inclua as palavras-chave da campanha nos títulos')));
                              }
                            }

                            if (needsMetaHelp) {
                              const hasImage = !!(ad.media_urls && ad.media_urls[0]);
                              const hasVideo = !!ad.video_url;

                              // ① Visual content — only if ad has no image/video
                              if (!hasImage && !hasVideo) {
                                suggestions.push(String(t('ads.adStrength.suggestions.moreContent', 'Adicione imagem ou vídeo ao anúncio (visual é crítico no Meta)')));
                              }
                              // ② Engagement rate specifically low — revise CTA/title
                              if (engRanking.includes('BELOW_AVERAGE')) {
                                suggestions.push(String(t('ads.adStrength.suggestions.improveRelevance', 'Engagement baixo — revise título + CTA pra serem mais específicos pra sua audiência')));
                              }
                              // ③ Quality low + only 1 ad — suggest creating variations
                              const metaAdCount = ads.filter(a => a.external_id_meta).length;
                              if (qRanking.includes('BELOW_AVERAGE') && metaAdCount <= 1) {
                                suggestions.push(String(t('ads.adStrength.suggestions.testCreatives', 'Crie variações do anúncio pra Meta testar qual performa melhor')));
                              }
                            }

                            return (
                              <>
                                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                  {/* Google: approval + ad strength */}
                                  {googleLive?.approvalStatus && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${approvalColor(googleLive.approvalStatus)}`} title={`Review: ${googleLive.reviewStatus || '—'}`}>
                                      Google: {String(t(`ads.adStrength.approvalValues.${googleLive.approvalStatus}`, googleLive.approvalStatus))}
                                    </span>
                                  )}
                                  {googleLive?.adStrength && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${strengthColor(googleLive.adStrength)}`} title={t('ads.adStrengthTooltip', 'Qualidade do anúncio (Google)')}>
                                      ⚡ {String(t(`ads.adStrength.strengthValues.${googleLive.adStrength}`, googleLive.adStrength))}
                                    </span>
                                  )}
                                  {/* Meta: effective status + review + quality rankings */}
                                  {metaLive?.effectiveStatus && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      metaLive.effectiveStatus === 'ACTIVE' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                      metaLive.effectiveStatus === 'DISAPPROVED' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                                      metaLive.effectiveStatus === 'PENDING_REVIEW' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                      'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                    }`} title={metaLive.effectiveStatus}>
                                      Meta: {String(t(`ads.adStrength.metaEffectiveValues.${metaLive.effectiveStatus}`, metaLive.effectiveStatus))}
                                    </span>
                                  )}
                                  {metaLive?.qualityRanking && metaLive.qualityRanking !== 'UNKNOWN' && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${rankingColor(metaLive.qualityRanking)}`} title={t('ads.metaQualityRanking', 'Quality Ranking (Meta)')}>
                                      ⚡ Q: {String(t(`ads.adStrength.rankingValues.${metaLive.qualityRanking}`, metaLive.qualityRanking))}
                                    </span>
                                  )}
                                  {metaLive?.engagementRateRanking && metaLive.engagementRateRanking !== 'UNKNOWN' && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${rankingColor(metaLive.engagementRateRanking)}`} title={t('ads.metaEngagementRanking', 'Engagement Rate Ranking (Meta)')}>
                                      E: {String(t(`ads.adStrength.rankingValues.${metaLive.engagementRateRanking}`, metaLive.engagementRateRanking))}
                                    </span>
                                  )}
                                  {metaLive?.issues && metaLive.issues.length > 0 && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 flex items-center gap-1" title={JSON.stringify(metaLive.issues)}>
                                      <AlertTriangle className="w-3 h-3" />
                                      {metaLive.issues.length} {t('ads.issues', 'problema(s)')}
                                    </span>
                                  )}
                                  {/* LinkedIn */}
                                  {linkedinLive?.status && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      linkedinLive.status === 'ACTIVE' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                                      linkedinLive.status === 'PAUSED' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                                      'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                    }`} title={linkedinLive.status}>
                                      LinkedIn: {String(t(`ads.adStrength.linkedinValues.${linkedinLive.status}`, linkedinLive.status))}
                                    </span>
                                  )}
                                  {linkedinLive?.reviewStatus && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${approvalColor(linkedinLive.reviewStatus)}`} title={linkedinLive.reviewStatus}>
                                      LI: {String(t(`ads.adStrength.approvalValues.${linkedinLive.reviewStatus}`, linkedinLive.reviewStatus))}
                                    </span>
                                  )}
                                </div>

                                {/* Performance metrics (last 30 days) — pulled from AdMetric */}
                                {(ad as any).metrics30d && ((ad as any).metrics30d.impressions > 0 || (ad as any).metrics30d.clicks > 0) && (
                                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
                                    <div className="px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                                      <div className="text-[10px] uppercase tracking-wide text-gray-400">{t('ads.metrics.impressions', 'Impressões')}</div>
                                      <div className="font-semibold text-gray-800 dark:text-gray-200">{Number((ad as any).metrics30d.impressions || 0).toLocaleString('pt-BR')}</div>
                                    </div>
                                    <div className="px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                                      <div className="text-[10px] uppercase tracking-wide text-gray-400">{t('ads.metrics.clicks', 'Cliques')}</div>
                                      <div className="font-semibold text-gray-800 dark:text-gray-200">{Number((ad as any).metrics30d.clicks || 0).toLocaleString('pt-BR')}</div>
                                    </div>
                                    <div className="px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                                      <div className="text-[10px] uppercase tracking-wide text-gray-400">CTR</div>
                                      <div className="font-semibold text-gray-800 dark:text-gray-200">{(ad as any).metrics30d.ctr}%</div>
                                    </div>
                                    <div className="px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                                      <div className="text-[10px] uppercase tracking-wide text-gray-400">CPC</div>
                                      <div className="font-semibold text-gray-800 dark:text-gray-200">R$ {(ad as any).metrics30d.cpc}</div>
                                    </div>
                                    <div className="px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                                      <div className="text-[10px] uppercase tracking-wide text-gray-400">{t('ads.metrics.spend', 'Gasto')}</div>
                                      <div className="font-semibold text-gray-800 dark:text-gray-200">R$ {(ad as any).metrics30d.spend}</div>
                                    </div>
                                    <div className="px-2 py-1 rounded-md bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700">
                                      <div className="text-[10px] uppercase tracking-wide text-gray-400">{t('campaigns.rmf.adConversions', 'Conversões')}</div>
                                      <div className="font-semibold text-gray-800 dark:text-gray-200">{Number((ad as any).metrics30d.conversions || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}</div>
                                    </div>
                                  </div>
                                )}

                                {/* Actionable improvement suggestions — merges:
                                    (a) static heuristic tips (always present when applicable)
                                    (b) AI + Google recommendations loaded via the "Analisar" button */}
                                {(() => {
                                  const aiRecs = adRecsByAdId[ad.id];
                                  const aiCards = [...(aiRecs?.ai || []), ...(aiRecs?.google || [])];
                                  const totalCount = suggestions.length + aiCards.length;
                                  if (totalCount === 0) return null;
                                  return (
                                    <details className="mt-2 group/sug">
                                      <summary className="cursor-pointer text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1 list-none py-0.5 hover:text-amber-800 dark:hover:text-amber-300">
                                        <ChevronRight className="w-3 h-3 transition-transform group-open/sug:rotate-90" />
                                        💡 {t('ads.adStrength.suggestions.title', 'Dicas pra melhorar o anúncio')} ({totalCount})
                                      </summary>
                                      <div className="mt-1.5 pl-4 space-y-2">
                                        {aiCards.map((rec) => {
                                          const sourceLabel = rec.source === 'ai'
                                            ? t('campaigns.recommendations.sourceAi', 'IA')
                                            : rec.source === 'google'
                                              ? t('campaigns.recommendations.sourceGoogle', 'Google Ads')
                                              : rec.source === 'meta'
                                                ? t('campaigns.recommendations.sourceMeta', 'Meta Ads')
                                                : rec.source;
                                          const sourceClass = rec.source === 'ai'
                                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                                            : rec.source === 'google'
                                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                              : rec.source === 'meta'
                                                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                                                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
                                          return (
                                          <div key={rec.id} className="text-xs p-2 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 break-words min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                              <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${sourceClass}`}>
                                                  {sourceLabel}
                                                </span>
                                                <span className="font-semibold text-gray-800 dark:text-gray-100">{rec.title}</span>
                                              </div>
                                              {rec.priority && (
                                                <span className={`text-[9px] px-1 py-0.5 rounded font-bold uppercase ${
                                                  rec.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                                                  rec.priority === 'low' ? 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' :
                                                  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                                }`}>
                                                  {rec.priority === 'high' ? t('campaigns.recommendations.priorityHigh', 'Alta') :
                                                   rec.priority === 'low' ? t('campaigns.recommendations.priorityLow', 'Baixa') :
                                                   t('campaigns.recommendations.priorityMedium', 'Média')}
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-gray-600 dark:text-gray-300 mt-1 leading-relaxed">{rec.description}</p>
                                          </div>
                                          );
                                        })}
                                        {suggestions.length > 0 && (
                                          <ul className="space-y-0.5 text-xs text-gray-600 dark:text-gray-300 list-disc list-inside">
                                            {suggestions.map((s, i) => (
                                              <li key={i}>{s}</li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    </details>
                                  );
                                })()}
                              </>
                            );
                          })()}
                        </div>
                        {/* Ações: no celular ficam embaixo, botões dividem a largura */}
                        <div className="flex items-center gap-2 flex-shrink-0 [&>*]:flex-1 sm:[&>*]:flex-none [&>*]:justify-center">
                          {ad.status === 'active' && (
                            <button
                              onClick={() => handleAnalyzeAd(ad)}
                              aria-label={t('ads.analyzeAd', 'Analisar este anúncio com IA')}
                              disabled={adAnalyzing === ad.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                              title={t('ads.analyzeAd', 'Analisar este anúncio com IA')}
                            >
                              {adAnalyzing === ad.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                              <span className="hidden sm:inline">{t('ads.analyze', 'Analisar')}</span>
                            </button>
                          )}
                          <button
                            onClick={() => setViewingAd(ad)}
                            aria-label={t('ads.viewDetails', 'Ver detalhes')}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors"
                            title={t('ads.viewDetails', 'Ver detalhes')}
                          >
                            <Eye className="w-4 h-4" />
                            <span className="hidden sm:inline">{t('ads.view', 'Ver')}</span>
                          </button>
                          <Link
                            to={`/campaigns/${id}/edit?step=creatives&adId=${ad.id}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors"
                            title={t('ads.editAd', 'Editar anúncio')}
                            aria-label={t('ads.editAd', 'Editar anúncio')}
                          >
                            <Edit className="w-4 h-4" />
                            <span className="hidden sm:inline">{t('common.edit', 'Editar')}</span>
                          </Link>
                          {canToggle && (
                            <button
                              onClick={() => handleToggleAdStatus(ad)}
                              disabled={togglingAdIds.has(ad.id)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                                ad.status === 'active'
                                  ? 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50'
                                  : 'text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900/30 dark:hover:bg-green-900/50'
                              } disabled:opacity-50`}
                              title={isActivating ? t('ads.activateAd') : t('ads.pauseAd')}
                              aria-label={isActivating ? t('ads.activateAd') : t('ads.pauseAd')}
                            >
                              {togglingAdIds.has(ad.id) ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : isActivating ? (
                                <Play className="w-4 h-4" />
                              ) : (
                                <Pause className="w-4 h-4" />
                              )}
                              <span className="hidden sm:inline">
                                {isActivating ? t('ads.activateAd') : t('ads.pauseAd')}
                              </span>
                            </button>
                          )}
                          {(ad.external_id_google || ad.external_id_meta) && ad.status !== 'archived' && (
                            <button
                              onClick={() => handleRemoveAdFromPlatform(ad)}
                              disabled={removingAdIds.has(ad.id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors text-red-700 bg-red-50 hover:bg-red-100 dark:text-red-300 dark:bg-red-900/20 dark:hover:bg-red-900/40 disabled:opacity-50"
                              title={t('campaigns.rmf.adRemove', 'Remover da plataforma')}
                              aria-label={t('campaigns.rmf.adRemove', 'Remover da plataforma')}
                            >
                              {removingAdIds.has(ad.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                              <span className="hidden sm:inline">{t('campaigns.rmf.adRemove', 'Remover da plataforma')}</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        );
      })()}

      {activeTab === 'keywords' && id && (
        <CampaignKeywordsHub campaignId={id} campaignStatus={campaign?.status} />
      )}

      {activeTab === 'search-terms' && (
        <Card padding={false} className="p-4 sm:p-6">
          <div className="mb-4">
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              {t('campaigns.tabs.searchTerms', 'Termos de Busca')}
            </CardTitle>
          </div>
          <CardContent>
            {loadingSearchTerms ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary-500" /></div>
            ) : searchTerms.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-2">
                  {t('campaigns.searchTerms.noData', 'Nenhum termo de pesquisa nos últimos 30 dias.')}
                </p>
                <p className="text-sm text-gray-400">
                  {t('campaigns.searchTerms.noDataHint', 'Os termos aparecem aqui quando usuários buscarem no Google e suas keywords corresponderem.')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full text-sm sm:text-base">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">{t('campaigns.searchTermsTable.term', 'Termo')}</th>
                      <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">{t('campaigns.rmf.stMatchColumn', 'Correspondência do termo')}</th>
                      <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">{t('campaigns.searchTermsTable.impressions', 'Impressões')}</th>
                      <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">{t('campaigns.searchTermsTable.clicks', 'Cliques')}</th>
                      <th className="hidden md:table-cell text-center py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">CTR</th>
                      <th className="hidden md:table-cell text-center py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">CPC</th>
                      <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">{t('campaigns.campaignKeywords.cost', 'Gasto')}</th>
                      <th className="hidden md:table-cell text-center py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">{t('campaigns.campaignKeywords.conversions', 'Conv.')}</th>
                      <th className="hidden md:table-cell text-center py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">{t('campaigns.campaignKeywords.costPerConv', 'Custo/Conv')}</th>
                      <th className="text-center py-2 sm:py-3 px-2 sm:px-4 text-xs font-medium text-gray-500 uppercase">{t('campaigns.searchTermsTable.actions', 'Ações')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {searchTerms.map((term) => (
                      <tr key={term.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="py-2 sm:py-4 px-2 sm:px-4">
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <span className="font-medium text-sm sm:text-base text-gray-900 dark:text-white break-words">{term.search_term}</span>
                            {term.added_as_keyword && <Badge variant="success" className="text-xs">Adicionada</Badge>}
                            {term.added_as_negative && <Badge variant="danger" className="text-xs">Negativa</Badge>}
                          </div>
                          {term.keyword_matched && <p className="text-xs text-gray-500 mt-1 break-words">{t('campaigns.searchTermsTable.matched', 'Correspondeu')}: {term.keyword_matched}</p>}
                        </td>
                        <td className="text-center py-2 sm:py-4 px-2 sm:px-4 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          {searchTermMatchLabel(t, (term as any).search_term_match_type)}
                        </td>
                        <td className="text-center py-2 sm:py-4 px-2 sm:px-4 text-gray-600 dark:text-gray-400">{term.impressions.toLocaleString()}</td>
                        <td className="text-center py-2 sm:py-4 px-2 sm:px-4 text-gray-600 dark:text-gray-400">{term.clicks.toLocaleString()}</td>
                        <td className="hidden md:table-cell text-center py-2 sm:py-4 px-2 sm:px-4 text-gray-600 dark:text-gray-400">{formatPercent(term.ctr)}</td>
                        <td className="hidden md:table-cell text-center py-2 sm:py-4 px-2 sm:px-4 text-gray-600 dark:text-gray-400">{formatCurrency(term.cpc)}</td>
                        <td className="text-center py-2 sm:py-4 px-2 sm:px-4 text-gray-600 dark:text-gray-400">{formatCurrency(term.cost)}</td>
                        <td className="hidden md:table-cell text-center py-2 sm:py-4 px-2 sm:px-4 text-gray-600 dark:text-gray-400">{((term as any).conversions ?? 0).toFixed ? ((term as any).conversions).toFixed(1) : (term as any).conversions}</td>
                        <td className="hidden md:table-cell text-center py-2 sm:py-4 px-2 sm:px-4 text-gray-600 dark:text-gray-400">{formatCurrency((term as any).cost_per_conversion)}</td>
                        <td className="text-center py-2 sm:py-4 px-2 sm:px-4">
                          {!term.added_as_keyword && !term.added_as_negative && (
                            <div className="relative flex items-center justify-center gap-1">
                              <button onClick={() => setMatchTypePicker(matchTypePicker?.termId === term.id && matchTypePicker?.type === 'positive' ? null : { termId: term.id, type: 'positive' })} className="p-1.5 text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors" title="Adicionar como palavra-chave"><Plus className="w-4 h-4" /></button>
                              <button onClick={() => setMatchTypePicker(matchTypePicker?.termId === term.id && matchTypePicker?.type === 'negative' ? null : { termId: term.id, type: 'negative' })} className="p-1.5 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors" title="Adicionar como negativa"><Ban className="w-4 h-4" /></button>
                              {matchTypePicker?.termId === term.id && (
                                <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-[140px]">
                                  <p className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                    {matchTypePicker.type === 'positive' ? t('campaigns.campaignKeywords.keyword', 'Keyword') : t('campaigns.campaignKeywords.negativeKeywords', 'Negativa')}
                                  </p>
                                  {(['exact', 'phrase', 'broad'] as const).map((mt) => (
                                    <button
                                      key={mt}
                                      onClick={() => handleAddAsKeyword(term, matchTypePicker.type, mt)}
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                                    >
                                      <span className={`inline-block w-2 h-2 rounded-full ${mt === 'exact' ? 'bg-green-500' : mt === 'phrase' ? 'bg-blue-500' : 'bg-yellow-500'}`} />
                                      {t(`campaigns.campaignKeywords.${mt}`, mt)}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Pagination */}
                {searchTermsTotal > SEARCH_TERMS_PER_PAGE && (
                  <div className="flex items-center justify-between flex-wrap gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-sm text-gray-500">
                      Página {searchTermsPage} de {Math.ceil(searchTermsTotal / SEARCH_TERMS_PER_PAGE)} ({searchTermsTotal} termos)
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSearchTermsPage(p => Math.max(1, p - 1))}
                        disabled={searchTermsPage <= 1}
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => setSearchTermsPage(p => p + 1)}
                        disabled={searchTermsPage >= Math.ceil(searchTermsTotal / SEARCH_TERMS_PER_PAGE)}
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'automation' && id && (
        <CampaignAutomationHub campaignId={id} />
      )}

      {activeTab === 'analytics' && id && campaign && (
        <CampaignAnalytics campaignId={id} campaign={campaign} />
      )}

      {activeTab === 'media' && id && (
        <Card padding={false} className="p-4 sm:p-6">
          <div className="mb-4">
            <CardTitle>{t('campaigns.tabs.media', 'Mídia da campanha')}</CardTitle>
          </div>
          <CardContent>
            <CampaignMediaGallery campaignId={id} />
          </CardContent>
        </Card>
      )}

      {activeTab === 'recommendations' && id && (
        <RecommendationsTab
          campaignId={id}
          isGoogle={hasGoogle}
          analysisState={analysisState}
          onAnalyze={handleAnalyze}
          onApplyGoogle={handleApplyGoogleRec}
          onDismiss={(recId) => setAnalysisState((s) => ({ ...s, dismissed: new Set([...Array.from(s.dismissed), recId]) }))}
        />
      )}

      {/* Ad Details Modal */}
      <Modal
        isOpen={viewingAd !== null}
        onClose={() => setViewingAd(null)}
        title={viewingAd?.name || t('ads.adDetails', 'Detalhes do Anúncio')}
        size="2xl"
        maxHeight
      >
        {viewingAd && (() => {
          const adPlatform = viewingAd.settings?.platform
            || (viewingAd.external_id_google ? 'google'
              : viewingAd.external_id_meta ? 'meta'
              : viewingAd.external_id_linkedin ? 'linkedin'
              : 'google');
          const headlines = (viewingAd.headlines || []).filter(h => h && h.trim());
          const descriptions = (viewingAd.descriptions || []).filter(d => d && d.trim());
          const finalUrl = viewingAd.final_url || '';
          const displayUrl = viewingAd.display_url || (finalUrl ? (() => {
            try { return new URL(finalUrl).hostname; } catch { return finalUrl; }
          })() : 'seusite.com');
          const image = viewingAd.media_urls?.[0];
          const cta = viewingAd.call_to_action || '';
          // Resolve per-ad publisher from the lists we fetched
          const metaPageId = (viewingAd.settings as any)?.meta_page_id;
          const selectedMetaPage = metaPageId
            ? metaPages.find(p => String(p.page_id) === String(metaPageId))
            : null;
          // IG: per-ad override first, else derived from the selected page
          const metaInstagramId = (viewingAd.settings as any)?.meta_instagram_id;
          const selectedInstagram = (() => {
            if (metaInstagramId !== undefined && metaInstagramId !== '') {
              return metaPages.find(p => String((p as any).instagram_actor_id) === String(metaInstagramId)) || null;
            }
            // Default to whatever IG is linked to the selected FB page
            if (selectedMetaPage && (selectedMetaPage as any).instagram_username) return selectedMetaPage;
            return null;
          })();
          const linkedinOrgUrn = (viewingAd.settings as any)?.linkedin_org_urn;
          const selectedLinkedinOrg = linkedinOrgUrn
            ? linkedinOrgs.find(o => o.organization_urn === linkedinOrgUrn)
            : null;
          const ctaLabel = (() => {
            const map: Record<string, string> = {
              'learn_more': 'Saiba Mais', 'LEARN_MORE': 'Saiba Mais',
              'shop_now': 'Comprar Agora', 'SHOP_NOW': 'Comprar Agora',
              'sign_up': 'Cadastre-se', 'SIGN_UP': 'Cadastre-se',
              'contact_us': 'Entre em Contato', 'CONTACT_US': 'Entre em Contato',
              'download': 'Baixar', 'DOWNLOAD': 'Baixar',
              'get_quote': 'Solicitar Orçamento', 'GET_QUOTE': 'Solicitar Orçamento',
              'book_now': 'Reservar', 'BOOK_TRAVEL': 'Reservar',
              'get_offer': 'Ver Oferta', 'GET_OFFER': 'Ver Oferta',
              'watch_more': 'Assistir Mais', 'WATCH_MORE': 'Assistir Mais',
              'apply_now': 'Candidatar-se', 'APPLY_NOW': 'Candidatar-se',
              'subscribe': 'Inscrever-se', 'SUBSCRIBE': 'Inscrever-se',
              'register': 'Registrar', 'REGISTER': 'Registrar',
              'request_demo': 'Solicitar Demo', 'REQUEST_DEMO': 'Solicitar Demo',
            };
            return map[cta] || cta || 'Saiba Mais';
          })();
          return (
          <div className="space-y-5">
            {/* Status & platform IDs */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={
                viewingAd.status === 'active' ? 'success' :
                viewingAd.status === 'paused' ? 'warning' :
                viewingAd.status === 'rejected' ? 'danger' :
                'gray'
              }>
                {viewingAd.status === 'active' ? t('ads.status.active') :
                 viewingAd.status === 'paused' ? t('ads.status.paused') :
                 viewingAd.status === 'draft' ? t('ads.status.draft') :
                 viewingAd.status === 'pending_review' ? t('ads.status.pendingReview') :
                 viewingAd.status === 'rejected' ? t('ads.status.rejected') :
                 viewingAd.status}
              </Badge>
              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 capitalize">
                {viewingAd.type}
              </span>
              {viewingAd.external_id_google && (
                <span className="text-xs px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                  Google: {viewingAd.external_id_google}
                </span>
              )}
              {viewingAd.external_id_meta && (
                <span className="text-xs px-2 py-1 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                  Meta: {viewingAd.external_id_meta}
                </span>
              )}
              {viewingAd.external_id_linkedin && (
                <span className="text-xs px-2 py-1 rounded-full bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300">
                  LinkedIn: {viewingAd.external_id_linkedin}
                </span>
              )}
            </div>

            {/* PREVIEW SECTION */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                {t('ads.preview', 'Prévia do anúncio')}
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                  ({adPlatform === 'google' ? 'Google Search' : adPlatform === 'meta' ? 'Meta Feed' : adPlatform === 'linkedin' ? 'LinkedIn' : adPlatform === 'openai' ? 'ChatGPT' : adPlatform})
                </span>
              </h4>

              {/* Google Search Ad preview */}
              {adPlatform === 'google' && (
                <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs font-bold text-gray-900 dark:text-white">Anúncio</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">·</span>
                    <span className="text-xs text-gray-700 dark:text-gray-300 break-all min-w-0">{displayUrl}</span>
                  </div>
                  <div className="text-xl text-[#1a0dab] dark:text-blue-400 font-normal mb-1 cursor-pointer hover:underline leading-tight break-words">
                    {headlines.slice(0, 3).join(' | ') || 'Seu título aparecerá aqui'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 leading-snug break-words">
                    {descriptions.slice(0, 2).join(' ') || 'Sua descrição aparecerá aqui.'}
                  </div>
                  {headlines.length > 3 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-400 italic">
                      {t('ads.googleRotationNote', 'O Google escolhe automaticamente qual combinação de {{headlines}} títulos e {{descs}} descrições exibir.', { headlines: headlines.length, descs: descriptions.length })}
                    </div>
                  )}
                </div>
              )}

              {/* Meta Feed Ad preview */}
              {adPlatform === 'meta' && (
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden max-w-md mx-auto shadow-sm">
                  <div className="p-3 flex items-center gap-2">
                    {selectedMetaPage?.page_picture ? (
                      <img src={selectedMetaPage.page_picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex-shrink-0 flex items-center justify-center text-white text-xs font-bold">
                        {selectedMetaPage?.page_name?.charAt(0).toUpperCase() || 'P'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {selectedMetaPage?.page_name || 'Sua Página'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <span>Patrocinado · 🌐</span>
                        {selectedInstagram && (selectedInstagram as any).instagram_username && (
                          <span className="inline-flex items-center gap-0.5 ml-1 text-purple-600 dark:text-purple-400">
                            · <Instagram className="w-3 h-3" /> @{(selectedInstagram as any).instagram_username}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  {descriptions[0] && (
                    <div className="px-3 pb-2 text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                      {descriptions[0]}
                    </div>
                  )}
                  <div className="bg-gray-100 dark:bg-gray-800 aspect-square flex items-center justify-center overflow-hidden">
                    {image ? (
                      <img src={image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-gray-400 text-sm">Sem imagem</div>
                    )}
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase truncate">{displayUrl}</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {headlines[0] || 'Seu título aqui'}
                      </p>
                    </div>
                    <button type="button" className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-semibold rounded hover:bg-gray-300 dark:hover:bg-gray-600 flex-shrink-0">
                      {ctaLabel}
                    </button>
                  </div>
                </div>
              )}

              {/* LinkedIn Sponsored Content preview */}
              {adPlatform === 'linkedin' && (
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden max-w-md mx-auto shadow-sm">
                  <div className="p-3 flex items-center gap-2">
                    {selectedLinkedinOrg?.logo_url ? (
                      <img src={selectedLinkedinOrg.logo_url} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded bg-gradient-to-br from-blue-600 to-blue-800 flex-shrink-0 flex items-center justify-center text-white font-bold">
                        {selectedLinkedinOrg?.name?.charAt(0).toUpperCase() || 'C'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {selectedLinkedinOrg?.name || 'Sua Empresa'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Promovido</p>
                    </div>
                  </div>
                  {descriptions[0] && (
                    <div className="px-3 pb-2 text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                      {descriptions[0]}
                    </div>
                  )}
                  {image && (
                    <div className="bg-gray-100 dark:bg-gray-800 overflow-hidden">
                      <img src={image} alt="" className="w-full object-cover" style={{ aspectRatio: '1.91/1' }} />
                    </div>
                  )}
                  <div className="p-3 bg-gray-50 dark:bg-gray-800/50">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2 break-words">
                      {headlines[0] || 'Seu título aqui'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 truncate">{displayUrl}</p>
                    <button type="button" className="w-full px-4 py-2 border border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 text-sm font-semibold rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20">
                      {ctaLabel}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Collapsible raw details */}
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center gap-1 list-none">
                <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                {t('ads.showRawDetails', 'Ver todos os campos')}
              </summary>
              <div className="mt-3 space-y-4 pl-5 border-l-2 border-gray-200 dark:border-gray-700">
                {/* Headlines */}
                {headlines.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase">
                      {t('campaigns.new.labels.headlines', 'Títulos')} ({headlines.length})
                    </h5>
                    <ol className="space-y-1 list-decimal list-inside text-sm text-gray-700 dark:text-gray-300">
                      {headlines.map((h, i) => (
                        <li key={i} className="break-words">{h}</li>
                      ))}
                    </ol>
                  </div>
                )}
                {/* Descriptions */}
                {descriptions.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase">
                      {t('campaigns.new.labels.descriptions', 'Descrições')} ({descriptions.length})
                    </h5>
                    <ol className="space-y-1 list-decimal list-inside text-sm text-gray-700 dark:text-gray-300">
                      {descriptions.map((d, i) => (
                        <li key={i} className="break-words">{d}</li>
                      ))}
                    </ol>
                  </div>
                )}
                {/* CTA */}
                {cta && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase">
                      {t('campaigns.new.callToAction', 'CTA')}
                    </h5>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{ctaLabel} <span className="text-xs text-gray-400">({cta})</span></p>
                  </div>
                )}
                {/* URLs */}
                {finalUrl && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase">
                      {t('campaigns.new.destinationUrl', 'URL de destino')}
                    </h5>
                    <a
                      href={finalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-700 break-all inline-flex items-center gap-1"
                    >
                      {finalUrl}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                )}
                {viewingAd.display_url && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase">
                      {t('ads.displayUrl', 'URL de exibição')}
                    </h5>
                    <p className="text-sm text-gray-700 dark:text-gray-300 break-all">{viewingAd.display_url}</p>
                  </div>
                )}
                {/* Media */}
                {viewingAd.media_urls && viewingAd.media_urls.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase">
                      {t('ads.media', 'Mídia')}
                    </h5>
                    <div className="grid grid-cols-3 gap-2">
                      {viewingAd.media_urls.map((url, i) => (
                        <img
                          key={i}
                          src={url}
                          alt={`Media ${i + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                        />
                      ))}
                    </div>
                  </div>
                )}
                {viewingAd.video_url && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase">
                      {t('ads.video', 'Vídeo')}
                    </h5>
                    <a
                      href={viewingAd.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:text-primary-700 break-all inline-flex items-center gap-1"
                    >
                      {viewingAd.video_url}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                  </div>
                )}
                {/* Keywords */}
                {viewingAd.settings?.keywords && Array.isArray(viewingAd.settings.keywords) && viewingAd.settings.keywords.length > 0 && (
                  <div>
                    <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase">
                      {t('campaigns.new.keywords', 'Palavras-chave')}
                    </h5>
                    <div className="flex flex-wrap gap-1">
                      {viewingAd.settings.keywords.map((kw: string, i: number) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Timestamps */}
                <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 pt-2">
                  <span>
                    {t('common.createdAt', 'Criado em')}: {new Date(viewingAd.created_at).toLocaleString()}
                  </span>
                  <span>
                    {t('common.updatedAt', 'Atualizado em')}: {new Date(viewingAd.updated_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </details>

            {/* Action buttons */}
            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setViewingAd(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {t('common.close', 'Fechar')}
              </button>
              <Link
                to={`/campaigns/${id}/edit?step=creatives`}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                {t('common.edit', 'Editar')}
              </Link>
            </div>
          </div>
          );
        })()}
      </Modal>

      {/* Snippet de conversão (visualizar tag/snippet de uma meta) */}
      <Modal
        isOpen={snippetModalOpen}
        onClose={() => setSnippetModalOpen(false)}
        title={`${t('campaigns.googleSnippet.title', 'Snippet de conversão')} — ${snippetName}`}
        size="xl"
        maxHeight
      >
        {snippetLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        ) : snippetData ? (
          <div className="space-y-4">
            {snippetData.snippets.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {snippetData.snippets.map((s, i) => (
                  <button
                    key={`${s.type}-${s.format}-${i}`}
                    type="button"
                    onClick={() => setSelectedSnippetIdx(i)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      selectedSnippetIdx === i
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {s.type} · {s.format}
                  </button>
                ))}
              </div>
            )}
            {(() => {
              const snip = snippetData.snippets[selectedSnippetIdx] || snippetData.primary || snippetData.snippets[0];
              if (!snip) return null;
              return (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('campaigns.googleSnippet.globalTag', 'Tag do Google (todas as páginas)')}
                      </p>
                      <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={<Code className="w-4 h-4" />}
                        onClick={() => copySnippet(snip.globalSiteTag)}
                      >
                        {t('campaigns.googleSnippet.copy', 'Copiar')}
                      </Button>
                    </div>
                    <pre className="text-xs font-mono text-gray-200 bg-gray-900 dark:bg-black/60 border border-gray-700 rounded-lg p-3 max-h-48 overflow-auto whitespace-pre-wrap break-all">
                      {snip.globalSiteTag}
                    </pre>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('campaigns.googleSnippet.eventSnippet', 'Snippet do evento (na conversão)')}
                      </p>
                      <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={<Code className="w-4 h-4" />}
                        onClick={() => copySnippet(snip.eventSnippet)}
                      >
                        {t('campaigns.googleSnippet.copy', 'Copiar')}
                      </Button>
                    </div>
                    <pre className="text-xs font-mono text-gray-200 bg-gray-900 dark:bg-black/60 border border-gray-700 rounded-lg p-3 max-h-48 overflow-auto whitespace-pre-wrap break-all">
                      {snip.eventSnippet}
                    </pre>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('campaigns.googleSnippet.note', 'Cole a Tag do Google em todas as páginas e o Snippet do evento na página/ação que conta como conversão.')}
                  </p>
                </>
              );
            })()}
          </div>
        ) : null}
      </Modal>

      {/* Criar nova meta de conversão */}
      <Modal
        isOpen={newGoalModalOpen}
        onClose={() => !creatingGoal && setNewGoalModalOpen(false)}
        title={t('campaigns.googleNewGoal.button', 'Criar nova meta')}
        size="md"
      >
        <div className="space-y-4">
          <Input
            label={t('campaigns.googleNewGoal.name', 'Nome da meta')}
            value={newGoalName}
            onChange={(e) => setNewGoalName(e.target.value)}
            disabled={creatingGoal}
            autoFocus
          />
          <Select
            label={t('campaigns.googleNewGoal.category', 'Categoria')}
            options={newGoalCategoryOptions}
            value={newGoalCategory}
            onChange={(e) => setNewGoalCategory(e.target.value)}
            disabled={creatingGoal}
          />
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="secondary"
              onClick={() => setNewGoalModalOpen(false)}
              disabled={creatingGoal}
            >
              {t('campaigns.googleNewGoal.cancel', 'Cancelar')}
            </Button>
            <Button
              onClick={handleCreateGoal}
              isLoading={creatingGoal}
              disabled={creatingGoal || !newGoalName.trim()}
              leftIcon={!creatingGoal ? <Plus className="w-4 h-4" /> : undefined}
            >
              {t('campaigns.googleNewGoal.create', 'Criar')}
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

/**
 * Recommendations tab — runs a campaign-wide AI analysis plus pulls native
 * recommendations from Google Ads and Meta. LinkedIn has no public
 * recommendations API so we leave it out.
 */
/**
 * Native Google Ads recommendations — pulled live from the Google Ads API
 * (campaign / ad group / ad / account level). Self-contained: fetches on mount,
 * groups into subsections, and supports Apply / Dismiss per card.
 */
const GoogleNativeRecommendations: React.FC<{ campaignId: string }> = ({ campaignId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [grouped, setGrouped] = useState<{
    campaign: GoogleRecommendation[];
    ad_group: GoogleRecommendation[];
    ad: GoogleRecommendation[];
    account: GoogleRecommendation[];
  } | null>(null);
  const [count, setCount] = useState(0);
  // resourceName currently being applied/dismissed
  const [busy, setBusy] = useState<Record<string, 'apply' | 'dismiss'>>({});

  const fetchRecs = async () => {
    setLoading(true);
    setErrorCode(null);
    setErrorMsg(null);
    try {
      const res = await campaignService.getGoogleRecommendations(campaignId);
      if (res.success && res.data) {
        setGrouped(res.data.grouped);
        setCount(res.data.count ?? res.data.items?.length ?? 0);
      } else {
        setErrorCode(res.code || null);
        setErrorMsg(res.message || null);
        setGrouped(null);
        setCount(0);
      }
    } catch (err: any) {
      setErrorCode(err?.response?.data?.code || null);
      setErrorMsg(err?.response?.data?.message || err?.message || null);
      setGrouped(null);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch once when the tab mounts.
  useEffect(() => {
    fetchRecs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const handleApply = async (rec: GoogleRecommendation) => {
    setBusy((b) => ({ ...b, [rec.resourceName]: 'apply' }));
    try {
      const res = await campaignService.applyGoogleRecommendation(campaignId, rec.resourceName);
      if (res.success) {
        toast.success(t('campaigns.googleRecs.applied', 'Sugestão aplicada no Google Ads'));
        await fetchRecs();
      } else {
        toast.error(res.message || t('campaigns.googleRecs.applyError', 'Falha ao aplicar sugestão'));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || t('campaigns.googleRecs.applyError', 'Falha ao aplicar sugestão'));
    } finally {
      setBusy((b) => {
        const next = { ...b };
        delete next[rec.resourceName];
        return next;
      });
    }
  };

  const handleDismiss = async (rec: GoogleRecommendation) => {
    setBusy((b) => ({ ...b, [rec.resourceName]: 'dismiss' }));
    try {
      const res = await campaignService.dismissGoogleRecommendation(campaignId, rec.resourceName);
      if (res.success) {
        toast.success(t('campaigns.googleRecs.dismissed', 'Sugestão dispensada'));
        await fetchRecs();
      } else {
        toast.error(res.message || t('campaigns.googleRecs.dismissError', 'Falha ao dispensar sugestão'));
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || t('campaigns.googleRecs.dismissError', 'Falha ao dispensar sugestão'));
    } finally {
      setBusy((b) => {
        const next = { ...b };
        delete next[rec.resourceName];
        return next;
      });
    }
  };

  const GoogleBadge = (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      Google Ads
    </span>
  );

  // Friendly note for connection-related codes — never crash.
  const connectionNote = (() => {
    if (errorCode === 'GOOGLE_ADS_NOT_CONNECTED') {
      return t('campaigns.googleRecs.notConnected', 'Conecte sua conta do Google Ads para ver as sugestões nativas do Google.');
    }
    if (errorCode === 'GOOGLE_ADS_NEEDS_RECONNECT') {
      return t('campaigns.googleRecs.needsReconnect', 'Reconecte sua conta do Google Ads para atualizar as sugestões.');
    }
    if (errorCode === 'NOT_GOOGLE') {
      // Not a Google campaign — hide the whole section silently.
      return null;
    }
    return null;
  })();

  // NOT_GOOGLE: render nothing.
  if (errorCode === 'NOT_GOOGLE') return null;

  // "Campanha" subsection combines campaign-level + account-level recs.
  const campaignCards = [...(grouped?.campaign || []), ...(grouped?.account || [])];
  const subsections: { key: string; title: string; cards: GoogleRecommendation[] }[] = grouped ? [
    { key: 'campaign', title: t('campaigns.googleRecs.levelCampaign', 'Campanha'), cards: campaignCards },
    { key: 'ad_group', title: t('campaigns.googleRecs.levelAdGroup', 'Grupo de anúncios'), cards: grouped.ad_group || [] },
    { key: 'ad', title: t('campaigns.googleRecs.levelAd', 'Anúncio'), cards: grouped.ad || [] },
  ] : [];

  const renderCard = (rec: GoogleRecommendation) => {
    const pot = rec.impact?.potential;
    const impactBits: string[] = [];
    if (pot) {
      if (pot.clicks > 0) impactBits.push(t('campaigns.googleRecs.impactClicks', '+{{n}} cliques', { n: Math.round(pot.clicks) }));
      if (pot.conversions > 0) impactBits.push(t('campaigns.googleRecs.impactConversions', '+{{n}} conv.', { n: Math.round(pot.conversions) }));
      if (pot.impressions > 0) impactBits.push(t('campaigns.googleRecs.impactImpressions', '+{{n}} impr.', { n: Math.round(pot.impressions) }));
    }
    const cardBusy = busy[rec.resourceName];
    return (
      <div key={rec.resourceName} className="rounded-xl border border-blue-200 dark:border-blue-800 bg-white dark:bg-gray-900 p-3 sm:p-4 flex flex-col gap-2 min-w-0 break-words">
        <h5 className="font-semibold text-gray-900 dark:text-white text-sm">{rec.label}</h5>
        {rec.hint && <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{rec.hint}</p>}
        {impactBits.length > 0 && (
          <p className="text-xs font-medium text-green-700 dark:text-green-400">
            {impactBits.join(' · ')}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <button
            onClick={() => handleApply(rec)}
            disabled={!!cardBusy}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {cardBusy === 'apply' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {t('campaigns.googleRecs.apply', 'Aplicar')}
          </button>
          <button
            onClick={() => handleDismiss(rec)}
            disabled={!!cardBusy}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {cardBusy === 'dismiss' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
            {t('campaigns.googleRecs.dismiss', 'Dispensar')}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-2xl border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 p-3 sm:p-5 space-y-4 min-w-0">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {t('campaigns.googleRecs.title', 'Sugestões do Google')}
          </h3>
          {GoogleBadge}
        </div>
        <button
          onClick={fetchRecs}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          {t('campaigns.googleRecs.refresh', 'Buscar sugestões do Google')}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-blue-100/60 dark:bg-blue-900/20 animate-pulse" />
          ))}
        </div>
      )}

      {/* Connection error (friendly) */}
      {!loading && connectionNote && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{connectionNote}</span>
        </div>
      )}

      {/* Generic error (non-connection) */}
      {!loading && !connectionNote && errorCode === null && errorMsg && !grouped && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
          {errorMsg}
        </div>
      )}

      {/* Empty state */}
      {!loading && !connectionNote && grouped && count === 0 && (
        <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
          {t('campaigns.googleRecs.empty', 'Nenhuma sugestão do Google no momento — elas aparecem conforme a campanha acumula dados.')}
        </div>
      )}

      {/* Grouped subsections */}
      {!loading && !connectionNote && grouped && count > 0 && (
        <div className="space-y-4">
          {subsections.map((sub) => {
            if (sub.cards.length === 0) return null;
            return (
              <div key={sub.key} className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {sub.title} <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({sub.cards.length})</span>
                </h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {sub.cards.map(renderCard)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface RecommendationsTabProps {
  campaignId: string;
  isGoogle: boolean;
  analysisState: {
    loading: boolean;
    result: CampaignAnalysisResult | null;
    error: string | null;
    dismissed: Set<string>;
  };
  onAnalyze: () => void;
  onApplyGoogle: (rec: RecommendationCard) => void;
  onDismiss: (recId: string) => void;
}

const RecommendationsTab: React.FC<RecommendationsTabProps> = ({ campaignId, isGoogle, analysisState, onAnalyze, onApplyGoogle, onDismiss }) => {
  const { t } = useTranslation();
  const { loading, result, error, dismissed } = analysisState;

  const allCards: { group: 'ai' | 'google' | 'meta'; cards: RecommendationCard[] }[] = result ? [
    { group: 'ai', cards: (result.ai || []).filter((c) => !dismissed.has(c.id)) },
    { group: 'google', cards: (result.google || []).filter((c) => !dismissed.has(c.id)) },
    { group: 'meta', cards: (result.meta || []).filter((c) => !dismissed.has(c.id)) }
  ] : [];

  const totalCount = result ? (result.ai.length + result.google.length + result.meta.length) : 0;

  const groupMeta: Record<'ai' | 'google' | 'meta', { label: string; icon: React.ReactNode; gradient: string; border: string; }> = {
    ai: {
      label: t('campaigns.recommendations.aiLabel', 'IA da DevSkin'),
      icon: <Sparkles className="w-4 h-4" />,
      gradient: 'from-purple-500 to-pink-500',
      border: 'border-purple-200 dark:border-purple-800'
    },
    google: {
      label: t('campaigns.recommendations.googleLabel', 'Google Ads'),
      icon: <Lightbulb className="w-4 h-4" />,
      gradient: 'from-blue-500 to-cyan-500',
      border: 'border-blue-200 dark:border-blue-800'
    },
    meta: {
      label: t('campaigns.recommendations.metaLabel', 'Meta Ads'),
      icon: <Lightbulb className="w-4 h-4" />,
      gradient: 'from-indigo-500 to-violet-500',
      border: 'border-indigo-200 dark:border-indigo-800'
    }
  };

  return (
    <div className="space-y-6">
      {/* Native Google Ads recommendations — only for Google campaigns */}
      {isGoogle && <GoogleNativeRecommendations campaignId={campaignId} />}

      {/* Analyze CTA */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-purple-50 via-white to-indigo-50 dark:from-purple-900/20 dark:via-gray-900 dark:to-indigo-900/20 p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('campaigns.recommendations.title', 'Recomendações inteligentes')}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {t('campaigns.recommendations.subtitle', 'Análise com IA + recomendações nativas do Google Ads e Meta Ads baseadas na performance dos últimos 30 dias.')}
              </p>
            </div>
          </div>
          <button
            onClick={onAnalyze}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 w-full md:w-auto whitespace-nowrap rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white font-semibold shadow-lg shadow-purple-500/30 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading
              ? t('campaigns.recommendations.analyzing', 'Analisando...')
              : result
                ? t('campaigns.recommendations.reanalyze', 'Analisar de novo')
                : t('campaigns.recommendations.analyze', 'Analisar com IA')}
          </button>
        </div>
        {result && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            {t('campaigns.recommendations.generatedAt', 'Gerado em')} {new Date(result.generatedAt).toLocaleString()} — {totalCount} {t('campaigns.recommendations.total', 'recomendação(ões) no total')}
          </p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !result && !error && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Lightbulb className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{t('campaigns.recommendations.emptyHint', 'Clique em "Analisar com IA" para ver sugestões personalizadas.')}</p>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !result && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ))}
        </div>
      )}

      {/* Results grouped */}
      {result && allCards.map(({ group, cards }) => {
        if (cards.length === 0) return null;
        const meta = groupMeta[group];
        return (
          <details key={group} className="group/rec rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50">
            <summary className="cursor-pointer list-none p-3 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-xl">
              <ChevronRight className="w-4 h-4 text-gray-500 transition-transform group-open/rec:rotate-90" />
              <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${meta.gradient} text-white flex items-center justify-center`}>
                {meta.icon}
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-white">{meta.label}</h4>
              <span className="text-xs text-gray-500 dark:text-gray-400">({cards.length})</span>
            </summary>
            <div className="px-3 pb-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
              {cards.map((rec) => (
                <div key={rec.id} className={`rounded-xl border ${meta.border} bg-white dark:bg-gray-900 p-3 sm:p-4 flex flex-col gap-2 min-w-0 break-words`}>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${
                      rec.source === 'ai'
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
                        : rec.source === 'google'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                          : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                    }`}>
                      {rec.source === 'ai'
                        ? t('campaigns.recommendations.sourceAi', 'IA')
                        : rec.source === 'google'
                          ? t('campaigns.recommendations.sourceGoogle', 'Google Ads')
                          : rec.source === 'meta'
                            ? t('campaigns.recommendations.sourceMeta', 'Meta Ads')
                            : rec.source}
                    </span>
                    {(rec as any).platform && (rec as any).platform !== 'all' && rec.source === 'ai' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 font-medium capitalize">
                        {(rec as any).platform}
                      </span>
                    )}
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <h5 className="font-semibold text-gray-900 dark:text-white text-sm">{rec.title}</h5>
                    {rec.priority && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${
                        rec.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                        rec.priority === 'low' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' :
                        'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                      }`}>
                        {rec.priority === 'high' ? t('campaigns.recommendations.priorityHigh', 'Alta') :
                         rec.priority === 'low' ? t('campaigns.recommendations.priorityLow', 'Baixa') :
                         t('campaigns.recommendations.priorityMedium', 'Média')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{rec.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {rec.source === 'google' && rec.applyResourceName && (
                      <button
                        onClick={() => onApplyGoogle(rec)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {t('campaigns.recommendations.apply', 'Aplicar')}
                      </button>
                    )}
                    <button
                      onClick={() => onDismiss(rec.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <X className="w-3.5 h-3.5" />
                      {t('campaigns.recommendations.dismiss', 'Dispensar')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </details>
        );
      })}

      {/* Empty-after-analysis message */}
      {result && totalCount === 0 && !loading && (
        <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
          {t('campaigns.recommendations.noneFound', 'Nenhuma recomendação encontrada no momento. Sua campanha parece estar bem configurada!')}
        </div>
      )}
    </div>
  );
};

export default CampaignDetailPage;
